import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import * as i1$1 from '@upr-web-primer/dal';
import { TokenStatus, DalModule } from '@upr-web-primer/dal';
import * as i6 from '@upr-web-primer/notification';
import { NotificationModule } from '@upr-web-primer/notification';
import * as i1 from '@upr-web-primer/page-labels';
import { PageLabelsModule } from '@upr-web-primer/page-labels';
import * as i5 from '@upr-web-primer/spinner';
import { SpinnerModule } from '@upr-web-primer/spinner';
import * as i3 from '@upr-web-primer/app-utils';
import { paramBool, genericRetryStrategy } from '@upr-web-primer/app-utils';
import { isEmpty, get, isBoolean, pickBy, negate, isUndefined, values, includes, sortBy, map, find } from 'lodash-es';
import { BehaviorSubject, take, Subscription, filter, firstValueFrom, from, switchMap, retry, catchError } from 'rxjs';
import * as i2 from '@upr-web-primer/locale';
import * as i4 from '@upr-web-primer/storage';
import { HttpHeaders } from '@angular/common/http';

const CacheKeys = {
    MARKETING_ID: 'marketing_id',
    InteractGuestId: 'InteractGuestId',
    GUESTS_E_TAG: 'GUESTS-ETAG',
    LOGGEDIN_GUEST_ID: 'LOGGEDIN_GUEST_ID',
    LOGGEDIN_USER_ID: 'LOGGEDIN_USER_ID',
    IS_SIGN_IN: 'IS_SIGN_IN',
    EMAIL_USER_ID: 'EMAIL_USER_ID',
    OAUTH_LOGIN: 'oAuthLogin',
    OAUTH_USER_NAME: 'oAuthLoginUserName',
    OAUTH_LOGIN_GID: 'OAUTH_LOGIN_GID',
    USER_L_DAP: 'USER_Ldap',
    USER_F_NAME: 'USER_FNAME',
    USER_L_NAME: 'USER_LNAME',
    RETAIN_NOTIFICATION: 'RETAIN_NOTIFICATION',
    STORE_ID: 'STORE_ID',
    CONTRACT_ID: 'CONTRACT_ID',
    HEADERS_WC_TOKEN: 'HEADERS_WC_TOKEN',
    HEADERS_WC_TRUSTED_TOKEN: 'HEADERS_WC_TRUSTED_TOKEN',
    PHONE_1_TYPE: 'PHONE_1_TYPE',
    ZIP_CODE: 'zip_code',
    PROMPT_TO_VERIFY_EMAIL: 'promptToVerifyEmail',
    EMAIL_TEXT_MESSAGE: 'emailTextMessage',
    EMAIL_CHANGED: 'EMAIL_CHANGED'
};

const GuestsUrls = {
    GUESTS: {
        BASE: '/guests',
        WITH_ID: '/guests/{guest_id}',
        AFFILIATIONS: '/guests/{guest_id}/affiliations',
        TRAVEL_PARTY_AFFILIATIONS: '/guests/{guest_id}/travel-party-affiliations',
        ACCOUNT_AFFILIATIONS: '/guests/{guest_id}/account-affiliations',
        PREFERENCES: '/guests/{guest_id}/preferences',
        USER_ID_UPDATE: '/guests/{guest_id}/user-id-update',
        PERSONAL_INFO: '/guests/{guest_id}/personal-info',
        PERSONAL_INFO_ADDRESSES: '/guests/{guest_id}/personal-info/addresses',
        PERSONAL_INFO_ADDRESSES_SINGLE: '/guests/{guest_id}/personal-info/addresses/{address_id}',
        PERSONAL_INFO_CONTACTS: '/guests/{guest_id}/personal-info/contacts',
        PERSONAL_INFO_EMAILS: '/guests/{guest_id}/personal-info/emails',
        PERSONAL_INFO_EMAILS_SINGLE: '/guests/{guest_id}/personal-info/emails/{email_address}',
        PERSONAL_INFO_PHONES: '/guests/{guest_id}/personal-info/phones',
        PERSONAL_INFO_PHONES_SINGLE: '/guests/{guest_id}/personal-info/phones/{phone_number}',
    },
    INSTANCES: {
        CREATE: '{base_url_secondary}/instances',
    },
    MARKETING_IDS: {
        RETRIEVE: '/marketing-ids',
        WITH_ID: '/marketing-ids/{marketing_id}',
    },
    AUTH: {
        FORGOT_PASSWORD: '/cred/auth/forgotPassword/{user_id}',
        RESET_PASSWORD: '/cred/auth/resetPassword/{user_id}/{request_id}',
        CHANGE_PASSWORD: '/cred/auth/changePassword/{user_id}',
    },
    ENTITLEMENTS: {
        ASSIGNEES: '/guest/TravelParty/retrieveAssignedNames?guestId=',
        CREATE: '/guest/TravelParty/createAssignee',
        LINK: '/et/Entitlements/link',
        ENTITLEMENTS_LINK_METHODS: '/et/Entitlements/link/methods',
        ENTITLEMENTS_LINK_SEARCH_LINK: '/et/Entitlements/search/link',
    },
    FOLIO: {
        GET_FOLIO: '/folio/Folios/{guest_id}',
        CARDS: '/folio/Folios/{guest_id}/cards',
        SET_PIN: '/folio/Folios/{guest_id}/pin',
        TRANSACTIONS: '/folio/Folios/{guest_id}/transactions',
        ADD_CARD: '/pay/payment/wallet',
        DELETE_CARD: '/folio/Folios/{guest_id}/card/{card_id}',
        SET_PRIMARY_CARD: '/folio/Folios/card',
        ALERT_PREF: '/folio/Folios/{guest_id}/alert/preferences',
        SPENDING_LIMIT: '/folio/Folios/{guest_id}/limit',
        WALLET: '/inv/Orders/wallet',
    },
    COMMERCE: {
        SHIPPING_ADDRESS_SAVE: '/shop/wcs/resources/store/{store_id}/person/@self/contact',
        AUTHN: '/guest/GuestProfiles/commerce/authN',
    },
    TRIDION: {
        ID_EQV_NAME: '/cp/content/pages?ids=',
    },
    ACCOUNT_ENROLL: '/enroll-account',
    ACCOUNT_DELETE: '/enroll-account/{user_id}',
    UNENROLL_ACCOUNT: '/guests/{guest_id}/unenroll-account',
    CREATE_CONTACT: '/create-contact',
    EMAIL_OPT_IN_OUT: '/email-opt-in-out',
    ORDER_PROFILE_TO_ACCOUNT: '/order-profile-to-account',
    PROFILE_TO_ACCOUNT: '/profile-to-account',
    CONTRACT: '/shop/wcs/resources/store/{store_id}/contract?q=eligible',
    VALIDATE_EMAIL: '/utils/utilities/validateEmail',
    ACCOUNT_AVAILABLE: '/check-account',
    CREATE_UNREGISTERED: '/guest/GuestProfiles/unregistered',
    CREATE_LINKED_ACCOUNT: '/guest/GuestProfiles/createLinkedAccount',
    WALLET: {
        PAYMENT_CARD: '/guests/{guest_id}/payment-cards/{payment_card_id}',
        PAYMENT_CARDS: '/guests/{guest_id}/payment-cards',
        UPDATE_PAYMENT_CARD: '/guests/{guest_id}/payment-cards/{payment_card_id}',
        MAKE_PRIMARY_CARD: '/guests/{guest_id}/payment-cards/{payment_card_id}/make-primary',
        SET_PIN: '/guests/{guest_id}/pin',
        HAS_PIN: '/guests/{guest_id}/has-pin',
        LINK_TICKET: '/guests/{guest_id}/link-ticket',
        TICKETS: '/guests/{guest_id}/guest-travel-party-member-tickets?resort-area-code={resort_area_code}',
    },
    TRAVEL_PARTY: {
        SET_SPENDING_LIMIT: '/guests/{guest_id}/travel-party-members/{travel_party_member_id}/spending-limit',
        TRANSACTIONS: '/guests/{guest_id}/travel-party-members-receipts',
        TRAVEL_PARTY_MEMBERS: '/guests/{guest_id}/travel-party-members',
    },
};
const USHSourceIds = {
    en: {
        WebCard: '1002025',
        WebSpendLimit: '1002026',
        WebPIN: '1002027',
        WebTravelPartyMemeber: '1002028',
        WebAccountCreation: '1012000',
        WebAssignEntitlements: '1002002',
        WebPurchaseConfirmation: '1002003',
        WebEditCommunications: '1012004',
        WebEditContactInfo: '1012005',
        WebEditPersonalInfo: '1012006',
        WebEditAddress: '1012007',
        WebCheckout: '1002008',
        WebEmailUnsubOrEditInterest: '1012023',
        WebLinkEntitlements: '1012024',
        WebNewsLetter: {
            default: '1012010',
            footer: '1012011',
            wwohp: '1002012',
            hhn: '1002013',
            mg: '1002014',
            kingkong: '1002015',
            vb: '1002016',
            hotels: '1002017',
            rtu: '1002018',
            cw: '1002019',
            ioa: '1002020',
            usf: '1002021',
            bmg: '1002022',
            fastandfurious: '1002030',
            'fast-furious-supercharged': '1002030',
        },
        WebCreateUnregisteredUser: '1000002',
        WebCheckoutCreateUnregisteredUser: '1002008',
        WebPasswordUpdate: '1012063',
        WebPasswordReset: '1012061',
        WebForgotPassword: '1012060',
        WebAccountDelete: '1012066',
        WebUSHDiningReservation: '1012103',
        MobileUSHDiningReservation: '1016101',
    },
    es: {
        WebEditCommunications: '3002004',
        WebEditContactInfo: '3002005',
        WebEditPersonalInfo: '3002006',
        WebEditAddress: '3002007',
        WebEmailUnsubOrEditInterest: '3002023',
        WebNewsLetter: {
            default: '3002010',
            footer: '3002011',
            wwohp: '3002012',
            hhn: '3002013',
            mg: '3002014',
            kingkong: '3002015',
            vb: '3002016',
            hotels: '3002017',
            rtu: '3002018',
            cw: '3002019',
            ioa: '3002020',
            usf: '3002021',
            bmg: '3002022',
            fastandfurious: '3002030',
        },
        WebCreateUnregisteredUser: '3000002',
        WebCheckoutCreateUnregisteredUser: '3002008',
    },
};
const SourceIds = {
    en: {
        WebCard: '1002025',
        WebSpendLimit: '1002026',
        WebPIN: '1002027',
        WebTravelPartyMemeber: '1002028',
        WebAccountCreation: '1002001',
        WebAssignEntitlements: '1002002',
        WebPurchaseConfirmation: '1002003',
        WebEditCommunications: '1002004',
        WebEditContactInfo: '1002005',
        WebEditPersonalInfo: '1002006',
        WebEditAddress: '1002007',
        WebCheckout: '1002008',
        WebEmailUnsubOrEditInterest: '1002023',
        WebLinkEntitlements: '1002024',
        WebNewsLetter: {
            default: '1002010',
            footer: '1002011',
            wwohp: '1002012',
            hhn: '1002013',
            mg: '1002014',
            kingkong: '1002015',
            vb: '1002016',
            hotels: '1002017',
            rtu: '1002018',
            cw: '1002019',
            ioa: '1002020',
            usf: '1002021',
            bmg: '1002022',
            fastandfurious: '1002030',
            'fast-furious-supercharged': '1002030',
        },
        WebCreateUnregisteredUser: '1000002',
        WebCheckoutCreateUnregisteredUser: '1002008',
        WebPasswordUpdate: '1002063',
        WebPasswordReset: '1002061',
        WebForgotPassword: '1002060',
        WebAccountDelete: '1002066',
        WebDiningReservation: '1002103',
        MobileDiningReservation: '1006101',
    },
    es: {
        WebEditCommunications: '3002004',
        WebEditContactInfo: '3002005',
        WebEditPersonalInfo: '3002006',
        WebEditAddress: '3002007',
        WebEmailUnsubOrEditInterest: '3002023',
        WebNewsLetter: {
            default: '3002010',
            footer: '3002011',
            wwohp: '3002012',
            hhn: '3002013',
            mg: '3002014',
            kingkong: '3002015',
            vb: '3002016',
            hotels: '3002017',
            rtu: '3002018',
            cw: '3002019',
            ioa: '3002020',
            usf: '3002021',
            bmg: '3002022',
            fastandfurious: '3002030',
        },
        WebCreateUnregisteredUser: '3000002',
        WebCheckoutCreateUnregisteredUser: '3002008',
    },
};
const DefaultValue = {
    PRIMARY_SHIPPING_ADDRESS_INDICATOR: 'Y',
    CURRENCY_CODE: 'usd',
};

const MS_GUESTS_REPLACED_DEFAULTS = {
    ms_guests: {
        cart_enabled: paramBool('$ms_guests.cart_enabled$'),
        skip_instance: paramBool('$ms_guests.skip_instance$'),
    },
};
const MS_GUESTS_ENV_DEFAULTS = {
    ms_guests: {
        cart_enabled: true,
        skip_instance: false,
    },
};

var PhoneType;
(function (PhoneType) {
    PhoneType["cell"] = "cell";
    PhoneType["work"] = "work";
    PhoneType["home"] = "home";
})(PhoneType || (PhoneType = {}));
var StatusType;
(function (StatusType) {
    StatusType["active"] = "active";
    StatusType["inactive"] = "inactive";
})(StatusType || (StatusType = {}));
var EmailType;
(function (EmailType) {
    EmailType["personal"] = "personal";
    EmailType["work"] = "work";
})(EmailType || (EmailType = {}));
var AddressType;
(function (AddressType) {
    AddressType["home"] = "home";
    AddressType["work"] = "work";
    AddressType["shipping"] = "shipping";
    AddressType["billing"] = "billing";
    AddressType["generic"] = "generic";
})(AddressType || (AddressType = {}));

class MsGuestBaseHelper {
    constructor(pageLabelsService, localeService, appEnvSrv, localStorage, sessionStorage, spinner, notification, defaultValues, appDefaults, relativeUrls, tridionSite) {
        this.pageLabelsService = pageLabelsService;
        this.localeService = localeService;
        this.appEnvSrv = appEnvSrv;
        this.localStorage = localStorage;
        this.sessionStorage = sessionStorage;
        this.spinner = spinner;
        this.notification = notification;
        this.defaultValues = defaultValues;
        this.appDefaults = appDefaults;
        this.relativeUrls = relativeUrls;
        this.tridionSite = tridionSite;
        this.promise = {
            resolve: (response) => {
                return response ? Promise.resolve(response) : Promise.resolve();
            },
            reject: (msg = '', obj) => {
                if (!isEmpty(obj)) {
                    msg += JSON.stringify(obj);
                }
                return Promise.reject(msg);
            },
        };
        this.$emailInValidation = new BehaviorSubject('');
        this.$validationTimeout = new BehaviorSubject(false);
        this.loggedInStatusChanged = false;
        this._loggedInStatus = new BehaviorSubject(false);
        this._instanceLoaded = new BehaviorSubject(undefined);
        this.appEnvSrv.addDefaultValues(MS_GUESTS_ENV_DEFAULTS, MS_GUESTS_REPLACED_DEFAULTS);
        this.envConfig = this.appEnvSrv.getEnvVariables('ms_guests');
        this.loggedInStatus$ = this._loggedInStatus.asObservable();
        this.instanceLoaded$ = this._instanceLoaded.asObservable();
        if (this.pageLabelsService) {
            this.pageLabelsService.get().then((data) => (this.pageLabels = data));
        }
        if (isEmpty(this.defaultValues)) {
            this.consoleError('Please add values to injected provider: DefaultValues @' +
                this.constructor.name);
        }
        if (isEmpty(this.relativeUrls)) {
            this.consoleError('Please add values to injected provider: RelativeUrls @' +
                this.constructor.name);
        }
        this.instanceLoaded$
            .pipe(take(1))
            .subscribe(() => this.updateLoggedInStatus(this.isAuth()));
    }
    showLabel(error, state) {
        const found = this.findLabel(error);
        this.notification.show(found, state);
        return found;
    }
    findLabel(error) {
        if (this.pageLabels && this.pageLabels[error]) {
            return this.pageLabels[error];
        }
        return error;
    }
    getInstanceId() {
        return this.localStorage.getItem(CacheKeys.InteractGuestId);
    }
    consoleError(...args) {
        return console.error(...args);
    }
    checkGuestHeaders(response) {
        if (isEmpty(get(response, 'headers', undefined))) {
            return this.promise.reject('No headers available to set: ', response);
        }
        return this.promise.resolve(response);
    }
    setInstanceId(instanceId) {
        if (!instanceId) {
            return this.consoleError('Failed to set InteractGuestId: ', instanceId);
        }
        this.localStorage.setItem(CacheKeys.InteractGuestId, instanceId);
    }
    setEtag(etag) {
        if (!etag) {
            this.consoleError('Failed to set GUESTS-ETAG: ', etag);
        }
        this.localStorage.setItem(CacheKeys.GUESTS_E_TAG, etag);
    }
    catchError(error) {
        this.consoleError('ISSUE WITH MS-GUESTS CALL --->', get(error, 'message', undefined) || error);
        return this.promise.reject(error);
    }
    getNoCacheHeaders() {
        return {
            'x-uip': 'Web',
            'x-source-id': 'Web',
            Pragma: 'no-cache',
            'Cache-Control': 'no-store',
        };
    }
    getGuestId() {
        const guestId = this.localStorage.getItem(CacheKeys.LOGGEDIN_GUEST_ID) ||
            this.localStorage.getItem(CacheKeys.OAUTH_LOGIN_GID);
        if (!this.isLoggedIn() || !guestId) {
            return '';
        }
        return guestId;
    }
    getOAuthGuestID() {
        const guestId = this.localStorage.getItem(CacheKeys.OAUTH_LOGIN_GID);
        if (!this.isLoggedIn() || !guestId) {
            return '';
        }
        return guestId;
    }
    getLoginUserName() {
        let email = '';
        if (this.isLoggedIn()) {
            const guestEmail = this.localStorage.getItem(CacheKeys.OAUTH_USER_NAME) ||
                this.localStorage.getItem(CacheKeys.EMAIL_USER_ID);
            email = guestEmail ? guestEmail : '';
        }
        return email;
    }
    getMarketingId() {
        return this.localStorage.getItem(CacheKeys.MARKETING_ID);
    }
    isAuth() {
        return this.isLoggedIn() && !isEmpty(this.getOAuthGuestID());
    }
    getSourceId(sourceIdKey, language = '') {
        if (language === '') {
            language = this.localeService.getFromStorage().language;
        }
        return get(this.tridionSite === 'ush' ? USHSourceIds : SourceIds, language + '.' + sourceIdKey);
    }
    getUserId() {
        return this.getLoginUserName();
    }
    isLoggedIn() {
        return this.localStorage.getItem(CacheKeys.IS_SIGN_IN, true);
    }
    generateString(length) {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*_';
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return result;
    }
    getEtag() {
        return this.localStorage.getItem(CacheKeys.GUESTS_E_TAG) || '';
    }
    getldapId() {
        return this.localStorage.getItem(CacheKeys.USER_L_DAP) || '';
    }
    clearGuest(oAuth = false) {
        const user = this.getLoginUserName();
        if (oAuth) {
            this.localStorage.removeItem(CacheKeys.OAUTH_LOGIN);
            this.localStorage.removeItem(CacheKeys.OAUTH_USER_NAME);
            this.localStorage.removeItem(CacheKeys.OAUTH_LOGIN_GID);
        }
        this.localStorage.setItem(CacheKeys.IS_SIGN_IN, 'false');
        this.localStorage.removeItem(CacheKeys.EMAIL_USER_ID);
        this.localStorage.removeItem(CacheKeys.USER_F_NAME);
        this.localStorage.removeItem(CacheKeys.USER_L_NAME);
        this.localStorage.removeItem(CacheKeys.USER_L_DAP);
        this.localStorage.removeItem(CacheKeys.MARKETING_ID);
        this.localStorage.removeItem(CacheKeys.LOGGEDIN_GUEST_ID);
        this.localStorage.removeItem(CacheKeys.LOGGEDIN_USER_ID);
        this.localStorage.removeItem(CacheKeys.GUESTS_E_TAG);
        this.localStorage.removeItem(CacheKeys.InteractGuestId);
        this.localStorage.removeItem('sessionNumber');
        this.localStorage.removeItem('CART_ITEM_COUNT');
        this.sessionStorage.removeItem('CART_ITEM_COUNT');
        if (user) {
            const label = get(this.pageLabels, 'SU1', '{0} has been logged out.').replace('{0}', user);
            this.notification.show(label, 'success');
        }
    }
    clearAuthn() {
        this.localStorage.removeItem(CacheKeys.HEADERS_WC_TOKEN);
        this.localStorage.removeItem(CacheKeys.HEADERS_WC_TRUSTED_TOKEN);
    }
    getRelativeUrl(url) {
        return url
            .replace('{storeId}', this.localStorage.getItem(CacheKeys.STORE_ID) ||
            this.defaultValues.STORE_ID)
            .replace('{guestId}', this.getGuestId())
            .replace('{loginId}', this.localStorage.getItem(CacheKeys.EMAIL_USER_ID) || '');
    }
    addressToContact(addressInfo) {
        const type = (addressInfo.address_type || addressInfo.addressType) ===
            AddressType.billing;
        const addrType = type
            ? this.defaultValues.BILLING_ADDR
            : this.defaultValues.SHIPPING_ADDR;
        const getAddrSubType = type ? 'B' : 'S';
        const contact = {
            firstName: addressInfo.first_name || addressInfo.firstName || '',
            lastName: addressInfo.last_name || addressInfo.lastName || '',
            phone1: addressInfo.phone_number || '',
            city: addressInfo.city || '',
            state: addressInfo.state || '',
            country: addressInfo.country || '',
            addressLine: [
                addressInfo.address_line1 || addressInfo.addressLine1 || '',
            ],
            email1: addressInfo.email_address || addressInfo.email || '',
            xaddr_addressField1: addressInfo.address_id ||
                addressInfo.addressId ||
                this.defaultValues.SOURCE_NO_VALIDATION,
            primary: (addressInfo.primary === true).toString(),
            addressType: getAddrSubType,
            nickName: [addrType, getAddrSubType, Date.now()].join('_'),
            zipCode: addressInfo.postal_code || addressInfo.zipPostalCode || '',
            phone1Type: this.localStorage.getItem(CacheKeys.IS_SIGN_IN) === 'true'
                ? '0'
                : this.localStorage.getItem(CacheKeys.PHONE_1_TYPE) || '1',
            sourceId: '',
        };
        contact.addressLine.push(addressInfo.address_line2 || addressInfo.addressLine2 || '');
        return contact;
    }
    replaceByPath(obj, path, value) {
        let schema = obj && typeof obj === 'object' ? obj : undefined;
        if (schema) {
            const pList = path.split('.');
            const len = pList.length;
            for (let i = 0; i < len - 1; i++) {
                const elem = pList[i];
                if (!schema[elem]) {
                    schema[elem] = {};
                }
                schema = schema[elem];
            }
            schema[pList[len - 1]] = value;
        }
        return schema;
    }
    updateLoggedInStatus(bool) {
        const val = this._loggedInStatus.getValue();
        if (val !== bool || this.loggedInStatusChanged) {
            this.loggedInStatusChanged = false;
            this._loggedInStatus.next(bool);
        }
    }
    loginPreCheck() {
        const val = this._loggedInStatus.getValue();
        if (this.isLoggedIn() !== val) {
            this.loggedInStatusChanged = true;
        }
    }
    updateInstanceLoaded(bool) {
        this._instanceLoaded.next(bool);
    }
}
MsGuestBaseHelper.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsGuestBaseHelper, deps: [{ token: i1.PageLabelsResolver }, { token: i2.LocaleService }, { token: i3.AppEnvironmentService }, { token: i4.LocalStorageService }, { token: i4.SessionStorageService }, { token: i5.SpinnerService }, { token: i6.NotificationService }, { token: 'DefaultValues' }, { token: 'AppDefaults' }, { token: 'RelativeUrls' }, { token: 'TridionSite' }], target: i0.ɵɵFactoryTarget.Injectable });
MsGuestBaseHelper.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsGuestBaseHelper, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsGuestBaseHelper, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.PageLabelsResolver }, { type: i2.LocaleService }, { type: i3.AppEnvironmentService }, { type: i4.LocalStorageService }, { type: i4.SessionStorageService }, { type: i5.SpinnerService }, { type: i6.NotificationService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['DefaultValues']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['AppDefaults']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['RelativeUrls']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['TridionSite']
                }] }]; } });

const SpinnerKeys = {
    ACCOUNT_ENROLL: 'AccountEnroll',
    ORDER_PROFILE_TO_ACCOUNT: 'orderProfileToAccount',
    PROFILE_TO_ACCOUNT: 'profileToAccount',
    OVERRIDE: 'override',
    LOGIN: 'login',
    EMAIL_OPT_IN_OUT: 'guestEmailOptInOut',
    FORGOT_PASSWORD: 'forgotPassword',
    GET_BY_GUEST_ID: 'getByGuestId',
    USER_ID_UPDATE: 'UserIdUpdate',
    CHANGE_PASSWORD: 'changePassword',
    UPDATE_PERSONAL_ADDRESS: 'updatePersonalAddress',
    DELETE_PERSONAL_ADDRESS: 'deletePersonalAddress',
    UPDATE_PERSONAL_CONTACT_INFO: 'updatePersonalContactInfo',
    UPDATE_PREFERENCES: 'updatePreferences',
    UPDATE_USER_PREFERENCES: 'UPDATE_USER_PREFERENCES',
    UPDATE_USER_PREFERENCES_BY_EMAIL_ID: 'UPDATE_USER_PREFERENCES_BY_EMAIL_ID',
    LOAD_CARDS: 'loadCards',
    SET_CARD_PIN: 'setCardPin',
    ADD_PAYMENT_CARD: 'addPaymentCard',
    DELETE_PAYMENT_CARD: 'deletePaymentCard',
    CHANGE_ALERT_PREFERENCES: 'changeAlertPreferences',
    SET_SPENDING_LIMIT: 'setSpendingLimit',
    GET_WALLET: 'getWallet',
    CREATE_GUEST_CONTACT: 'CreateGuestContact',
    COMMERCE_ADDRESS_REQUEST: 'submitCommerceAddressRequest',
    GET_PRIMARY_ADDRESS: 'getPrimaryAddress',
    SAVE_CARD_TO_ACC: 'SAVE_CARD_TO_ACC',
    MODIFY_USER_ADDRESS: 'MODIFY_USER_ADDRESS',
    GET_ACCOUNT_AVAILABLE: 'GET_ACCOUNT_AVAILABLE',
    CHECK_ACCOUNT_AVAILABLE: 'CHECK_ACCOUNT_AVAILABLE',
    SET_COMMERCE_ADDRESS_REQUEST: 'SET_COMMERCE_ADDRESS_REQUEST',
    CREATE_ASSIGNEE: 'CREATE_ASSIGNEE',
    UPDATE_USER_CONTACT: 'UPDATE_USER_CONTACT',
    GET_ENTITLEMENT_DETAILS: 'GET_ENTITLEMENT_DETAILS',
    RESET_PASSWORD: 'RESET_PASSWORD',
    CREATE_USER_ACCOUNT: 'CREATE_USER_ACCOUNT',
    LINK_ENTITLEMENT: 'LINK_ENTITLEMENT',
    GET_LINK_ENTITLEMENT: 'GET_LINK_ENTITLEMENT',
    ENTITLEMENTS_LINK_SEARCH_LINK: 'ENTITLEMENTS_LINK_SEARCH_LINK',
    GET_ENTITLEMENT_ASSIGNEES: 'GET_ENTITLEMENT_ASSIGNEES',
    VALIDATE_EMAIL: 'VALIDATE_EMAIL',
    ON_SUBMIT: 'ON_SUBMIT',
    GET_TRANSACTION_HISTORY: 'GET_TRANSACTION_HISTORY',
    SET_PRIMARY_PAYMENT_CARD: 'SET_PRIMARY_PAYMENT_CARD',
    GET_TICKETS: 'getTickets',
    GET_TRAVEL_PARTY_MEMBERS: 'GET_TRAVEL_PARTY_MEMBERS',
    SET_TRAVEL_PARTY_MEMBERS: 'SET_TRAVEL_PARTY_MEMBERS',
    HAS_PIN: 'HAS_PIN',
    GET_PAYMENT_CARD: 'GET_PAYMENT_CARD',
    UNENROLL_ACCOUNT: 'UNENROLL_ACCOUNT',
};

class MsGuestsBaseService {
    constructor(dal, helper) {
        this.dal = dal;
        this.helper = helper;
        this.TYPE_POST = 'POST';
        this.TYPE_DELETE = 'DELETE';
        this.TYPE_PUT = 'PUT';
        this.TYPE_PATCH = 'PATCH';
        this.TYPE_GET = 'GET';
        this.urls = GuestsUrls;
        this.cacheClear = false;
    }
    doRequest(url, type, request, headers, timeout, retry, get_response_headers = false, use_secure_api = false) {
        const secure_url = this.helper.appDefaults.SECURE_URL;
        const instanceId = this.helper.getInstanceId();
        const checkHeaders = type === this.TYPE_PUT ||
            type === this.TYPE_DELETE ||
            type === this.TYPE_POST ||
            !isEmpty(headers);
        let httpHeaders = new HttpHeaders();
        if (!checkHeaders && this.cacheClear) {
            headers = headers || {};
            Object.assign(headers, this.helper.getNoCacheHeaders());
        }
        if (typeof headers === 'object') {
            for (const [key, value] of Object.entries(headers)) {
                httpHeaders = httpHeaders.set(key, value);
            }
        }
        if (instanceId) {
            httpHeaders = httpHeaders.set('x-instance-id', instanceId);
        }
        return this.dal
            .httpRequest(url, type ? type : this.TYPE_POST, request, httpHeaders, timeout, retry, true, use_secure_api ? secure_url : '')
            .then((response) => {
            if ((checkHeaders && response && response?.headers) ||
                (type === this.TYPE_GET && response?.headers.get('etag'))) {
                const etag = response.headers.get('etag');
                if (etag) {
                    this.helper.setEtag(etag);
                }
            }
            this.cacheClear = checkHeaders && !this.cacheClear;
            if (type === this.TYPE_DELETE && response.status === 204) {
                return response;
            }
            return get_response_headers
                ? response
                : get(response, 'body', response);
        });
    }
    replaceKey(str, store) {
        return Object.entries(store).reduce((initial, [key, value]) => {
            return initial.replace(`{${key}}`, value);
        }, str);
    }
}
MsGuestsBaseService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsGuestsBaseService, deps: [{ token: i1$1.DalService }, { token: MsGuestBaseHelper }], target: i0.ɵɵFactoryTarget.Injectable });
MsGuestsBaseService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsGuestsBaseService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsGuestsBaseService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.DalService }, { type: MsGuestBaseHelper }]; } });

class ProfileMsGuestsService extends MsGuestsBaseService {
    getByGuestId(guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.WITH_ID, { guest_id }), this.TYPE_GET, undefined, headers, undefined, undefined, true);
    }
    updateByGuestId(request, guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.WITH_ID, { guest_id }), this.TYPE_PUT, request, headers);
    }
    deleteByGuestId(guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.WITH_ID, { guest_id }), this.TYPE_PUT, undefined, headers);
    }
}
ProfileMsGuestsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ProfileMsGuestsService, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
ProfileMsGuestsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ProfileMsGuestsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ProfileMsGuestsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ProfileMsGuestsClass {
    constructor(service, helper) {
        this.service = service;
        this.helper = helper;
    }
    deleteByGuestId(guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id || !etag) {
            return this.helper.promise.reject('Missing guest_id, or etag: ', {
                guest_id,
                etag,
            });
        }
        headers['If-Match'] = etag;
        return this.service.deleteByGuestId(guest_id, headers).then((response) => {
            this.helper.clearGuest(true);
            this.helper.updateLoggedInStatus(false);
            return response;
        });
    }
    getByGuestId(guest_id) {
        if (!this.helper.isAuth()) {
            return this.helper.promise.reject('Need oAuth login first');
        }
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id');
        }
        this.helper.spinner.show(SpinnerKeys.GET_BY_GUEST_ID);
        this.helper.loginPreCheck();
        return this.service
            .getByGuestId(guest_id, this.helper.getNoCacheHeaders())
            .then((response) => {
            if (isBoolean(response)) {
                return this.helper.promise.reject('OAuth has expired');
            }
            if (isEmpty(response.body)) {
                return this.helper.promise.reject('No guest data found for: ' + guest_id + '. Data returned is: ', response);
            }
            if (response && response.headers) {
                const etag = response.headers.get('etag') || '';
                this.helper.setEtag(etag);
            }
            this.helper.localStorage.setItem(CacheKeys.IS_SIGN_IN, 'true');
            this.helper.localStorage.setItem(CacheKeys.MARKETING_ID, get(response.body, 'marketing_id', ''));
            this.helper.localStorage.setItem(CacheKeys.EMAIL_USER_ID, get(response.body, 'user_id', ''));
            this.helper.localStorage.setItem(CacheKeys.OAUTH_USER_NAME, get(response.body, 'user_id', ''));
            this.helper.localStorage.setItem(CacheKeys.USER_L_DAP, get(response.body, 'ldap_id', ''));
            this.helper.localStorage.setItem(CacheKeys.USER_F_NAME, get(response.body, 'personal_info.contacts[0].first_name'));
            this.helper.localStorage.setItem(CacheKeys.USER_L_NAME, get(response.body, 'personal_info.contacts[0].last_name'));
            this.helper.localStorage.setItem(CacheKeys.LOGGEDIN_GUEST_ID, this.helper.getOAuthGuestID());
            this.helper.localStorage.setItem(CacheKeys.ZIP_CODE, get(response.body, 'guestProfile.contact.zipCode'));
            this.helper.updateLoggedInStatus(true);
            this.helper.spinner.hide(SpinnerKeys.GET_BY_GUEST_ID);
            return response.body;
        })
            .catch((error) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.promise.reject(error);
        });
    }
    updateByGuestId(request, sourceId, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id || !sourceId || !etag) {
            return this.helper.promise.reject('Missing guest_id, sourceId, or etag: ', { guest_id, sourceId, etag });
        }
        headers['x-source-id'] = sourceId;
        headers['If-Match'] = etag;
        return this.service.updateByGuestId(request, guest_id, headers);
    }
}
ProfileMsGuestsClass.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ProfileMsGuestsClass, deps: [{ token: ProfileMsGuestsService }, { token: MsGuestBaseHelper }], target: i0.ɵɵFactoryTarget.Injectable });
ProfileMsGuestsClass.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ProfileMsGuestsClass, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ProfileMsGuestsClass, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ProfileMsGuestsService }, { type: MsGuestBaseHelper }]; } });

class AccountMsGuestsService extends MsGuestsBaseService {
    createInstance(headers, base_url_secondary, timeout, retry) {
        return this.doRequest(this.replaceKey(this.urls.INSTANCES.CREATE, { base_url_secondary }), this.TYPE_POST, undefined, headers, timeout, retry, true);
    }
    createContact(request, headers, timeout, retry) {
        return this.doRequest(this.urls.CREATE_CONTACT, this.TYPE_POST, request, headers, timeout, retry, true);
    }
    createUnregistered(request) {
        return this.doRequest(this.urls.CREATE_UNREGISTERED, this.TYPE_PUT, request);
    }
    login(user_id, password, redirectUrl, sourceId) {
        return this.dal.oAuthLogin(user_id, password, redirectUrl, sourceId);
    }
    enroll(request, headers) {
        request.guests_request.personal_info.emails = [
            {
                email_address: request.creds.user_id,
                primary: true,
            },
        ];
        return this.doRequest(this.urls.ACCOUNT_ENROLL, this.TYPE_POST, request, headers, undefined, undefined, true);
    }
    delete(user_id) {
        return this.doRequest(this.replaceKey(this.urls.ACCOUNT_DELETE, { user_id }), this.TYPE_DELETE).then((response) => {
            return response;
        });
    }
    orderProfileToAccount(request, headers) {
        return this.doRequest(this.urls.ORDER_PROFILE_TO_ACCOUNT, this.TYPE_POST, request, headers);
    }
    profileToAccount(request, headers) {
        return this.doRequest(this.urls.PROFILE_TO_ACCOUNT, this.TYPE_POST, request, headers);
    }
    createLinkedAccount(request, headers = {}) {
        return this.doRequest(this.urls.CREATE_LINKED_ACCOUNT, this.TYPE_POST, request, headers);
    }
    emailOptInOut(request, headers, timeout, retry) {
        return this.doRequest(this.urls.EMAIL_OPT_IN_OUT, this.TYPE_POST, request, headers, timeout, retry, true);
    }
    validateEmail(request, timeout) {
        return this.doRequest(this.urls.VALIDATE_EMAIL, this.TYPE_POST, request, undefined, timeout);
    }
    checkAccountAvailable(email_address) {
        return this.doRequest(this.urls.ACCOUNT_AVAILABLE, this.TYPE_POST, {
            email_address,
        });
    }
    forgotPassword(user_id) {
        const sourceId = this.helper.getSourceId('WebForgotPassword');
        const headers = {
            'x-source-id': sourceId,
        };
        return this.doRequest(this.replaceKey(this.urls.AUTH.FORGOT_PASSWORD, { user_id }), this.TYPE_GET, undefined, headers);
    }
    resetPassword(request) {
        const sourceId = this.helper.getSourceId('WebPasswordReset');
        const headers = {
            'x-source-id': sourceId,
        };
        return this.doRequest(this.replaceKey(this.urls.AUTH.RESET_PASSWORD, {
            user_id: request.loginId,
            request_id: request.requestId,
        }), this.TYPE_PUT, request, headers);
    }
    changePassword(request, user_id) {
        const sourceId = this.helper.getSourceId('WebPasswordUpdate');
        const headers = {
            'x-source-id': sourceId,
        };
        return this.doRequest(this.replaceKey(this.urls.AUTH.CHANGE_PASSWORD, { user_id }), this.TYPE_PUT, request, headers, undefined, undefined, true);
    }
    logout() {
        return this.dal
            .oAuthLogout(true)
            .then(() => true)
            .catch(() => false);
    }
    authN(externalGuestId) {
        return this.doRequest(this.urls.COMMERCE.AUTHN, this.TYPE_POST, { externalGuestId }, {}, undefined, undefined, true).then((resp) => resp.body || resp);
    }
    loadContractId(store_id) {
        return this.doRequest(this.replaceKey(this.urls.CONTRACT, { store_id }), this.TYPE_GET);
    }
    updateByUserId(request, guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.USER_ID_UPDATE, { guest_id }), this.TYPE_POST, request, headers);
    }
    unenrollAccount(guest_id, sourceId) {
        if (!sourceId) {
            sourceId = this.helper.getSourceId('WebAccountDelete');
        }
        const headers = {
            'x-source-id': sourceId,
        };
        return this.doRequest(this.replaceKey(this.urls.UNENROLL_ACCOUNT, { guest_id }), this.TYPE_POST, undefined, headers);
    }
    getAffiliations(guest_id, headers, active, ticket_id) {
        const params = pickBy({ active, ticket_id }, negate(isUndefined));
        let urlParams = new URLSearchParams(Object.entries(params)).toString();
        if (urlParams) {
            urlParams = `?${urlParams}`;
        }
        const url = this.replaceKey(this.urls.GUESTS.AFFILIATIONS, { guest_id }) + urlParams;
        return this.doRequest(url, this.TYPE_GET, undefined, headers);
    }
    getTravelPartyAffiliations(guest_id, headers, active) {
        const params = pickBy({ active }, negate(isUndefined));
        let urlParams = new URLSearchParams(Object.entries(params)).toString();
        if (urlParams) {
            urlParams = `?${urlParams}`;
        }
        const url = this.replaceKey(this.urls.GUESTS.TRAVEL_PARTY_AFFILIATIONS, {
            guest_id,
        }) + urlParams;
        return this.doRequest(url, this.TYPE_GET, undefined, headers);
    }
    getAccountAffiliations(guest_id, headers, active) {
        const params = pickBy({ active }, negate(isUndefined));
        let urlParams = new URLSearchParams(Object.entries(params)).toString();
        if (urlParams) {
            urlParams = `?${urlParams}`;
        }
        const url = this.replaceKey(this.urls.GUESTS.ACCOUNT_AFFILIATIONS, {
            guest_id,
        }) + urlParams;
        return this.doRequest(url, this.TYPE_GET, undefined, headers);
    }
}
AccountMsGuestsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AccountMsGuestsService, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
AccountMsGuestsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AccountMsGuestsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AccountMsGuestsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class AccountMsGuestsClass {
    constructor(appDefaults, service, profileClass, helper, zone, dal) {
        this.appDefaults = appDefaults;
        this.service = service;
        this.profileClass = profileClass;
        this.helper = helper;
        this.zone = zone;
        this.dal = dal;
        this.subscriptions$ = new Subscription();
        if (!this.helper.envConfig.skip_instance) {
            this.dal.appTokenSet$
                .pipe(filter((data) => data === TokenStatus.Refresh), take(1))
                .subscribe(async (data) => {
                if (data === TokenStatus.Refresh) {
                    if (!this.helper.getInstanceId()) {
                        this.createGuestInstance();
                        return;
                    }
                    this.helper.updateInstanceLoaded(true);
                }
            });
        }
        else {
            this.helper.updateInstanceLoaded(false);
        }
        this.subscriptions$.add(this.dal.appTokenSet$
            .pipe(filter((data) => data === TokenStatus.Reset || data === TokenStatus.Reset_Authn))
            .subscribe((data) => {
            if (data === TokenStatus.Reset) {
                this.helper.clearGuest(true);
                this.helper.clearAuthn();
                this.helper.updateLoggedInStatus(false);
            }
            else if (data === TokenStatus.Reset_Authn) {
                this.helper.clearAuthn();
            }
        }));
    }
    createGuestInstance() {
        if (this.helper.getInstanceId()) {
            return this.helper.promise.resolve();
        }
        if (this.helper.envConfig.skip_instance) {
            this.helper.updateInstanceLoaded(false);
            return this.helper.catchError('Skipping instance creation');
        }
        return this.service
            .createInstance(this.helper.getNoCacheHeaders(), this.dal.envConfig.base_url_secondary)
            .then((response) => this.helper.checkGuestHeaders(response))
            .then((response) => {
            const instanceId = response.headers.get('location');
            const eTag = response.headers.get('etag');
            if (!instanceId || !eTag) {
                return this.helper.promise.reject('Failed to retrieve correct headers.', { instanceId, eTag });
            }
            this.helper.setInstanceId(instanceId);
            this.helper.setEtag(eTag);
            this.helper.updateInstanceLoaded(true);
            return response;
        }, (error) => {
            this.helper.updateInstanceLoaded(false);
            this.helper.catchError(error);
        });
    }
    createGuestContact(request, sourceId, skipAccountCheck = false, instanceId = this.helper.getInstanceId(), headers = this.helper.getNoCacheHeaders(), timeout, retry) {
        if (!instanceId) {
            return this.helper.promise.reject('Missing instanceId: ', instanceId);
        }
        headers['x-source-id'] = sourceId;
        this.helper.spinner.show(SpinnerKeys.CREATE_GUEST_CONTACT);
        const email = request.user_id ||
            get(request.personal_info, 'emails[0].email_address', '');
        const getResponse = skipAccountCheck
            ? this.validateEmail(email)
            : this.checkAccountAvailable(email).then(() => this.validateEmail(email));
        return getResponse
            .then(() => {
            if (!request.personal_info.emails) {
                request.personal_info.emails = [
                    {
                        email_address: email,
                        primary: true,
                    },
                ];
            }
            delete request.user_id;
            return this.service
                .createContact(request, headers, timeout, retry)
                .then((response2) => {
                this.helper.spinner.hide(SpinnerKeys.CREATE_GUEST_CONTACT);
                return response2;
            }, (error) => this.helper.catchError(error));
        })
            .catch((error) => {
            this.helper.spinner.show(SpinnerKeys.OVERRIDE);
            return this.helper.promise.reject(error);
        });
    }
    createUnregistered(request) {
        return this.service
            .createUnregistered(request)
            .then((status) => {
            if (status.statusCode === 200) {
                this.helper.showLabel('SU12', 'success');
            }
            return status;
        })
            .catch((error) => {
            const errorMessage = get(error, 'error.message', this.helper.pageLabels[error]);
            this.helper.showLabel(errorMessage);
            return this.helper.promise.reject(errorMessage);
        });
    }
    enroll(request, sourceId, instanceId = this.helper.getInstanceId(), headers = this.helper.getNoCacheHeaders()) {
        if (!instanceId) {
            return this.helper.promise.reject('Missing instanceId: ', instanceId);
        }
        headers['x-instance-id'] = instanceId;
        headers['x-source-id'] = sourceId;
        this.helper.spinner.show(SpinnerKeys.ACCOUNT_ENROLL);
        return this.service
            .enroll(request, headers)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.ACCOUNT_ENROLL);
            this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
            this.helper.showLabel('SU2', 'success');
            return response;
        })
            .catch((error) => {
            let errorMessage = 'ER71';
            const message = get(error, 'error.message', error);
            if (error.status === 409 ||
                (message && message.indexOf('Conflict') > -1)) {
                errorMessage = 'ER20';
            }
            this.helper.showLabel(errorMessage);
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.promise.reject(error);
        });
    }
    deleteAccount(user_id = this.helper.getUserId()) {
        return this.service.delete(user_id).then((response) => {
            this.reset();
            return response.status === 204;
        });
    }
    unenrollAccount(guest_id = this.helper.getGuestId(), sourceId = this.helper.getSourceId('WebAccountDelete')) {
        this.helper.spinner.show(SpinnerKeys.UNENROLL_ACCOUNT);
        return this.service
            .unenrollAccount(guest_id, sourceId)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.UNENROLL_ACCOUNT);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER71', 'error');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.promise.reject(error);
        });
    }
    orderProfileToAccount(request, sourceId, redirectUrl, bypassAuth = false, headers = this.helper.getNoCacheHeaders(), retry = 3) {
        headers['x-source-id'] = sourceId;
        this.helper.spinner.show(SpinnerKeys.ORDER_PROFILE_TO_ACCOUNT);
        return this.service
            .orderProfileToAccount(request, headers)
            .catch((error) => this.handleAccountCreationError(error))
            .then((response) => {
            if (!get(response, 'guest_id')) {
                const error = get(response, 'message');
                this.helper.showLabel(error);
                this.helper.spinner.hide(SpinnerKeys.ORDER_PROFILE_TO_ACCOUNT);
                return this.helper.promise.reject(error);
            }
            this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
            this.helper.showLabel('SU2', 'success');
            return this.login(request.user_id, request.password, redirectUrl).then((guestData) => {
                this.helper.spinner.hide(SpinnerKeys.ORDER_PROFILE_TO_ACCOUNT);
                return guestData;
            });
        })
            .then(() => {
            if (!bypassAuth) {
                return this.authN();
            }
            return;
        })
            .catch((error) => {
            if (error === 'ER71' && retry === 0) {
                this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
                this.helper.showLabel(error);
                return this.helper.promise.reject(error);
            }
            return new Promise((resolve) => {
                this.zone.runOutsideAngular(() => {
                    setTimeout(() => {
                        resolve(this.orderProfileToAccount(request, sourceId, redirectUrl, bypassAuth, headers, retry - 1));
                    }, 5000);
                });
            });
        });
    }
    profileToAccount(email, request, sourceId, redirectUrl, signUpLocation = 'register-account', headers = this.helper.getNoCacheHeaders()) {
        headers['x-source-id'] = sourceId;
        this.helper.spinner.show(SpinnerKeys.PROFILE_TO_ACCOUNT);
        return this.service
            .profileToAccount(request, headers)
            .catch((error) => signUpLocation === 'package-create-account'
            ? this.handlePackageCreateAccountError(error)
            : this.handleRegisterAccountError(error))
            .then((response) => {
            if (!get(response, 'guest_id')) {
                const error = get(response, 'message');
                this.helper.showLabel(error);
                this.helper.spinner.hide(SpinnerKeys.PROFILE_TO_ACCOUNT);
                return this.helper.promise.reject(error);
            }
            this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
            this.helper.showLabel('SU2', 'success');
            return this.login(email, request.password, redirectUrl);
        })
            .then((guestData) => {
            this.helper.spinner.hide(SpinnerKeys.PROFILE_TO_ACCOUNT);
            return guestData;
        })
            .catch((error) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            this.helper.showLabel(error);
            return this.helper.promise.reject(error);
        });
    }
    createLinkedAccount(request, sourceId, redirectUrl, headers = this.helper.getNoCacheHeaders(), retry = 3) {
        const oldRequest = {
            sourceId: sourceId,
            customerOrderId: request.customer_order_id,
            guestId: this.helper.getInstanceId(),
            userId: request.user_id,
            password: request.password,
        };
        this.helper.spinner.show(SpinnerKeys.ORDER_PROFILE_TO_ACCOUNT);
        return this.service
            .createLinkedAccount(oldRequest, headers)
            .catch((error) => this.handleAccountCreationError(error))
            .then((response) => {
            if (!get(response, 'result.guestProfile')) {
                const error = get(response, 'message');
                this.helper.showLabel(error);
                this.helper.spinner.hide(SpinnerKeys.ORDER_PROFILE_TO_ACCOUNT);
                return this.helper.promise.reject(error);
            }
            this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
            this.helper.showLabel('SU2', 'success');
            return this.login(request.user_id, request.password, redirectUrl).then((guestData) => {
                this.helper.spinner.hide(SpinnerKeys.ORDER_PROFILE_TO_ACCOUNT);
                return guestData;
            });
        })
            .then(() => this.authN())
            .catch((error) => {
            if (error === 'ER71' && retry === 0) {
                this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
                this.helper.showLabel(error);
                return this.helper.promise.reject(error);
            }
            return new Promise((resolve) => {
                this.zone.runOutsideAngular(() => {
                    setTimeout(() => {
                        resolve(this.createLinkedAccount(request, sourceId, redirectUrl, headers, retry - 1));
                    }, 5000);
                });
            });
        });
    }
    login(username, password, redirectUrl, activateAuthN = false, sourceId) {
        if (!username || !password) {
            return this.helper.promise.reject('Username and Password are mandatory');
        }
        this.helper.spinner.show(SpinnerKeys.LOGIN);
        let userInfo = {};
        this.helper.loginPreCheck();
        return this.service
            .login(username, password, redirectUrl, sourceId)
            .catch((error) => {
            let message = error && typeof error != 'object' ? error.toLowerCase() : '';
            switch (message) {
                case 'account locked':
                    message = this.helper.showLabel('ER28');
                    break;
                case 'invalid username':
                    message = this.helper.showLabel('ER24');
                    break;
                default:
                    message = this.helper.showLabel('ER71');
            }
            return this.helper.promise.reject(message);
        })
            .then((response) => {
            if (response !== 'Login success') {
                return this.helper.promise.reject(response === 'invalid username'
                    ? this.helper.showLabel('ER24')
                    : this.helper.showLabel('ER71'));
            }
            return;
        })
            .then(() => {
            this.helper.clearAuthn();
            if (activateAuthN) {
                return this.authN();
            }
            return;
        })
            .then(() => {
            const guest_id = this.helper.getGuestId();
            return this.profileClass.getByGuestId(guest_id);
        })
            .then((user) => {
            if (user && user.user_id && user.ldap_id) {
                userInfo = user;
            }
            return;
        })
            .then(() => {
            this.helper.spinner.hide(SpinnerKeys.LOGIN);
            return userInfo;
        })
            .catch((error) => {
            this.helper.showLabel(error ? error : 'ER71');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        })
            .finally(() => {
            this.helper.updateLoggedInStatus(this.helper.isAuth());
        });
    }
    loadContractId() {
        if (!this.helper.envConfig.cart_enabled) {
            return Promise.resolve('');
        }
        if (!this.helper?.defaultValues?.STORE_ID) {
            return this.helper.promise.reject('Missing STORE_ID in injected provider: defaultValues');
        }
        const store_id = this.helper.defaultValues.STORE_ID;
        this.helper.localStorage.setItem(CacheKeys.STORE_ID, store_id);
        const contract_id = this.helper.localStorage.getItem(CacheKeys.CONTRACT_ID);
        if (this.helper.localStorage.getItem(CacheKeys.CONTRACT_ID)) {
            return Promise.resolve(contract_id);
        }
        return this.service.loadContractId(store_id).then((response) => {
            if (isEmpty(response)) {
                return this.helper.promise.reject('Missing data from loadContractId: ', response);
            }
            const contract_id = values(get(response, 'contracts'))[0];
            this.helper.localStorage.setItem(CacheKeys.CONTRACT_ID, contract_id);
            return contract_id;
        });
    }
    async authN(instance_id = this.helper.getInstanceId()) {
        if (!this.helper.envConfig.cart_enabled) {
            return Promise.resolve();
        }
        if (this.helper.localStorage.getItem(CacheKeys.HEADERS_WC_TOKEN)) {
            return this.loadContractId();
        }
        const retryStr = genericRetryStrategy();
        return firstValueFrom(from(this.createGuestInstance()).pipe(switchMap(() => this.service.authN(instance_id || this.helper.getInstanceId())), switchMap((response) => {
            if (isEmpty(response)) {
                return this.helper.promise.reject('Missing data from authN: ', response);
            }
            this.helper.localStorage.setItem(CacheKeys.HEADERS_WC_TOKEN, get(response, 'result.WCToken'), { duration: { minutes: 30 } });
            this.helper.localStorage.setItem(CacheKeys.HEADERS_WC_TRUSTED_TOKEN, get(response, 'result.WCTrustedToken'), { duration: { minutes: 30 } });
            return from([response]);
        }), switchMap(() => this.loadContractId()), retry(retryStr), catchError((errorResponse) => {
            console.error(errorResponse);
            this.helper.clearAuthn();
            throw errorResponse;
        })));
    }
    guestEmailOptInOut(request, sourceId, instanceId = this.helper.getInstanceId(), headers = this.helper.getNoCacheHeaders(), timeout, retry) {
        if (!instanceId) {
            return this.helper.promise.reject('Missing instanceId: ', instanceId);
        }
        this.helper.spinner.show(SpinnerKeys.EMAIL_OPT_IN_OUT);
        headers['x-instance-id'] = instanceId;
        headers['x-source-id'] = sourceId;
        return this.service.emailOptInOut(request, headers, timeout, retry).then((response) => {
            const optIn = this.appDefaults.RESORT_NAME === 'UO'
                ? request.email_offers_promos
                : request.email_offers_promos_ush;
            if (optIn) {
                this.helper.showLabel('SU12', 'success');
            }
            else {
                this.helper.showLabel('SU11', 'success');
            }
            this.helper.spinner.hide(SpinnerKeys.EMAIL_OPT_IN_OUT);
            return response;
        }, (error) => {
            this.helper.showLabel('ER71');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    validateEmail(email, timeout) {
        const marketingId = get(this.helper.defaultValues, 'SOURCE_MARKETING_ID', undefined);
        if (!email || !marketingId) {
            return this.helper.promise.reject('Email or SOURCE_MARKETING_ID missing', { email, marketingId });
        }
        const timeoutDuration = timeout
            ? timeout
            : Number(get(this.helper.pageLabels, 'ValidateEmailTimeOut', 5000));
        const request = { email, marketingId };
        this.helper.spinner.show(SpinnerKeys.VALIDATE_EMAIL);
        return this.service
            .validateEmail(request, timeoutDuration)
            .then((response) => this.handleEmailValidation(response, email))
            .then(() => {
            this.helper.$validationTimeout.next(false);
            this.helper.spinner.hide(SpinnerKeys.VALIDATE_EMAIL);
        })
            .catch((error) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            const isTimeoutError = get(error, 'name', '') === 'TimeoutError';
            const currentTimeOutStatus = this.helper.$validationTimeout.getValue();
            if (isTimeoutError &&
                currentTimeOutStatus &&
                this.skipValidation(email)) {
                this.helper.$validationTimeout.next(false);
                return this.helper.promise.resolve('success');
            }
            else if (isTimeoutError && !currentTimeOutStatus) {
                this.skipValidation(email);
                this.helper.$validationTimeout.next(true);
                if (!!error && !!error.message)
                    error = this.helper.pageLabels['ER71'];
            }
            return this.helper.promise.reject(error);
        });
    }
    checkAccountAvailable(email) {
        if (!email) {
            return this.helper.promise.reject('Email Required');
        }
        this.helper.spinner.show(SpinnerKeys.CHECK_ACCOUNT_AVAILABLE);
        return this.service
            .checkAccountAvailable(email)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.CHECK_ACCOUNT_AVAILABLE);
            if (!response.is_account_available) {
                return this.helper.promise.reject(this.helper.showLabel('ER67'));
            }
            return response.is_account_available;
        })
            .catch((error) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.promise.reject(error);
        });
    }
    getAccountAvailable(request, sourceId, skipAccountCheck = false, instanceId = this.helper.getInstanceId(), headers, byPassOnTimeOut) {
        if (!request.g_recaptcha_response) {
            return this.helper.promise.reject('Recaptcha Required');
        }
        if (!instanceId) {
            return this.helper.promise.reject('Missing instanceId: ', instanceId);
        }
        this.helper.spinner.show(SpinnerKeys.GET_ACCOUNT_AVAILABLE);
        const getResponse = skipAccountCheck
            ? this.validateEmail(request.creds.user_id)
            : this.checkAccountAvailable(request.creds.user_id).then(() => this.validateEmail(request.creds.user_id));
        return getResponse
            .then((response) => {
            return this.goToEnroll(request, sourceId, instanceId, headers, response);
        })
            .catch((error) => {
            const isTimeoutError = get(error, 'name', '') === 'TimeoutError';
            if (isTimeoutError && byPassOnTimeOut) {
                return this.goToEnroll(request, sourceId, instanceId, headers);
            }
            this.helper.spinner.show(SpinnerKeys.OVERRIDE);
            return this.helper.promise.reject(error);
        });
    }
    goToEnroll(request, sourceId, instanceId, headers, response) {
        return this.enroll(request, sourceId, instanceId, headers).then((accountResponse) => {
            this.helper.spinner.hide(SpinnerKeys.GET_ACCOUNT_AVAILABLE);
            if (get(accountResponse, 'status') === 409) {
                this.helper.showLabel('ER67');
                return 'emailInUse';
            }
            return response;
        });
    }
    forgotPassword(user_id) {
        if (!user_id) {
            return this.helper.promise.reject('Needs a user email');
        }
        this.helper.spinner.show(SpinnerKeys.FORGOT_PASSWORD);
        return this.service
            .forgotPassword(user_id)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.FORGOT_PASSWORD);
            if (response === 'skip') {
                return;
            }
            const messageSent = get(response, 'message', '') === 'Email sent successfully';
            if (!messageSent) {
                return this.helper.showLabel('ER24');
            }
            this.helper.showLabel('SU3', 'success');
            return;
        })
            .catch((error) => {
            const responseCode = error && error.status === 500 ? 'ER71' : 'ER24';
            this.helper.showLabel(responseCode);
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    resetPassword(request) {
        if (!request ||
            !request.loginId ||
            !request.requestId ||
            !request.newPassword) {
            return this.helper.promise.reject('Please provide missing attributes for request; ', request);
        }
        this.helper.spinner.show(SpinnerKeys.RESET_PASSWORD);
        return this.service
            .resetPassword(request)
            .then((res) => {
            this.helper.spinner.hide(SpinnerKeys.RESET_PASSWORD);
            this.helper.showLabel('SU8', 'success');
            return res;
        })
            .catch((error) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.handleError(error);
        });
    }
    changePassword(request, user_id = this.helper.getUserId()) {
        if (!request || !request.newPassword || !request.oldPassword || !user_id) {
            return this.helper.promise.reject('Please provide missing attributes for request for: ' + user_id + ' - ', request);
        }
        this.helper.spinner.show(SpinnerKeys.CHANGE_PASSWORD);
        return this.service
            .changePassword(request, user_id)
            .then((response) => {
            if (!response || response.status === 403) {
                return this.helper.catchError(this.helper.showLabel('ER71'));
            }
            this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'success');
            this.helper.showLabel('SU8', 'success');
            this.helper.spinner.hide(SpinnerKeys.CHANGE_PASSWORD);
            return response;
        })
            .catch((error) => {
            let responseCode = 'ER71';
            if (error && (error.statusCode === 403 || error.status === 403)) {
                const message = get(error, 'message', '');
                if (message.indexOf('LDAP: error code 49') > -1 ||
                    message.indexOf('LDAP: error code 48') > -1) {
                    responseCode = 'ER8';
                }
                else if (message.indexOf('LDAP: error code 53') > -1) {
                    responseCode = 'ER28';
                }
            }
            this.helper.showLabel(responseCode);
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(responseCode);
        });
    }
    updateByUserId(request, sourceId, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        if (request.user_id !== request.user_id_confirm) {
            return this.helper.promise.reject('Email confirmation is the not same; ', request);
        }
        this.helper.spinner.show(SpinnerKeys.USER_ID_UPDATE);
        headers['x-source-id'] = sourceId;
        return this.service
            .updateByUserId(request, guest_id, headers)
            .then((response) => {
            this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, true);
            this.helper.localStorage.setItem(CacheKeys.EMAIL_CHANGED, true);
            this.helper.localStorage.setItem(CacheKeys.EMAIL_USER_ID, request.user_id);
            this.helper.localStorage.setItem(CacheKeys.OAUTH_USER_NAME, request.user_id);
            this.helper.showLabel('SU7', 'success');
            this.helper.spinner.hide(SpinnerKeys.USER_ID_UPDATE);
            return response;
        })
            .catch((error) => {
            let errorMessage = 'ER71';
            if (error.status >= 400 && error.status <= 408) {
                errorMessage = 'ER84';
            }
            if (error.status === 409) {
                errorMessage = 'ER61';
            }
            this.helper.showLabel(errorMessage);
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.promise.reject(error);
        });
    }
    logout() {
        this.helper.loginPreCheck();
        return this.service
            .logout()
            .then(async (response) => {
            if (response) {
                const userId = this.helper.getUserId();
                if (userId) {
                    this.helper.showLabel(this.helper.findLabel('SU1').replace('{0}', userId), 'success');
                }
                try {
                    return await this.reset();
                }
                catch (error) {
                    throw error;
                }
            }
            return this.helper.promise.reject('Error on logout: ', response);
        })
            .finally(() => {
            this.helper.updateLoggedInStatus(this.helper.isAuth());
        });
    }
    getAffiliations(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders(), active, ticket_id) {
        if (!this.helper.isLoggedIn()) {
            return Promise.reject(false);
        }
        headers['x-resort-area-code'] = get(this.appDefaults, 'RESORT_AREA_CODE');
        return this.service
            .getAffiliations(guest_id, headers, active, ticket_id)
            .catch((error) => this.helper.promise.reject(error));
    }
    getTravelPartyAffiliations(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders(), active) {
        if (!this.helper.isLoggedIn()) {
            return Promise.reject(false);
        }
        headers['x-resort-area-code'] = get(this.appDefaults, 'RESORT_AREA_CODE');
        return this.service
            .getTravelPartyAffiliations(guest_id, headers, active)
            .catch((error) => this.helper.promise.reject(error));
    }
    getAccountAffiliations(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders(), active) {
        if (!this.helper.isLoggedIn()) {
            return Promise.reject(false);
        }
        headers['x-resort-area-code'] = get(this.appDefaults, 'RESORT_AREA_CODE');
        return this.service
            .getAccountAffiliations(guest_id, headers, active)
            .catch((error) => this.helper.promise.reject(error));
    }
    normalizeEmailValidationResponse(response, override) {
        const result = get(response, 'result');
        if (!result || !result.verboseOutput) {
            return '';
        }
        let message = 'DEFAULT';
        const undeliverable = result.certainty === 'undeliverable';
        const overrideCheck = override && !undeliverable;
        switch (result.verboseOutput) {
            case 'unknown':
            case 'acceptAll':
            case 'verified':
            case 'timeout':
                message = 'A';
                break;
            case 'mailboxDisabled':
                message = overrideCheck ? 'A' : 'ER45';
                break;
            case 'mailboxDoesNotExist':
                message = overrideCheck ? 'A' : 'ER46';
                break;
            case 'mailboxFull':
                message = overrideCheck ? 'A' : 'ER47';
                break;
            case 'syntaxFailure':
                message = overrideCheck ? 'A' : 'ER48';
                break;
            case 'illegitimate':
            case 'unreachable':
            case 'unresolvable':
            case 'roleAccount':
            case 'typoDomain':
            case 'disposable':
                message = overrideCheck ? 'A' : 'ER49';
                break;
        }
        return message;
    }
    handleEmailValidation(response, email) {
        const responseType = !response || response === 'ignore'
            ? 'A'
            : this.normalizeEmailValidationResponse(response, this.helper.bypassEmailCheck);
        let error = this.helper.findLabel('ER6');
        switch (responseType) {
            case 'A':
                this.helper.bypassEmailCheck = false;
                return this.helper.promise.resolve('success');
            case 'ER45':
            case 'ER46':
            case 'ER47':
            case 'ER48':
            case 'ER49':
                this.helper.bypassEmailCheck = true;
                if (this.helper.$emailInValidation.getValue() != email)
                    error = this.helper.showLabel(responseType);
                break;
            default:
                this.helper.bypassEmailCheck = false;
                if (responseType !== 'Default') {
                    this.helper.showLabel(error);
                }
                break;
        }
        return !this.skipValidation(email)
            ? this.helper.promise.reject(error)
            : this.helper.promise.resolve('success');
    }
    handleError(error) {
        const statusCode = Number(get(error, 'statusCode', error.status));
        let errorCode = 'CONST_RESET_PASSWORD_MSG_ERROR';
        if (statusCode === 432) {
            errorCode = 'ER9';
        }
        else if (statusCode === 433 || statusCode === 436) {
            errorCode = 'ER86';
        }
        else if (statusCode === 434) {
            errorCode = 'ER60';
        }
        else if (statusCode === 435) {
            errorCode = 'ER51';
        }
        else if (statusCode === 400 && includes(error.message, 'has expired')) {
            this.helper.sessionStorage.setItem('RESET_PASSWORD_PRECLUDED', true);
            errorCode = 'ER123';
        }
        return this.helper.showLabel(errorCode);
    }
    handleAccountCreationError(error) {
        const errorMessage = get(error, 'error.message', error);
        const statusCode = Number(get(error, 'statusCode', error.status));
        let errorCode;
        switch (statusCode) {
            case 400:
                errorCode = 'ER20';
                if (errorMessage && errorMessage.indexOf('find') > -1) {
                    errorCode = 'ER71';
                }
                break;
            case 403:
                errorCode = 'ER8';
                break;
            case 432:
                errorCode = 'ER9';
                break;
            case 433:
            case 436:
                errorCode = 'ER86';
                break;
            case 437:
                errorCode = 'ER9';
                break;
            case 434:
                errorCode = 'ER60';
                break;
            case 435:
                errorCode = 'ER51';
                break;
            default:
                errorCode = 'ER71';
        }
        return this.helper.promise.reject(errorCode);
    }
    handlePackageCreateAccountError(error) {
        const errorMessage = get(error, 'error.message', '');
        const statusCode = Number(get(error, 'statusCode', error.status));
        let errorCode = 'ER71';
        if (statusCode === 400 || statusCode === 404) {
            errorCode = 'ER132';
            if (errorMessage.includes('already associated')) {
                errorCode = 'ER20';
            }
        }
        return this.helper.promise.reject(errorCode);
    }
    handleRegisterAccountError(error) {
        const errorMessage = get(error, 'error.message', '');
        const statusCode = Number(get(error, 'statusCode', error.status));
        let errorCode = 'ER71';
        if (statusCode === 400 || statusCode === 404) {
            errorCode = 'ER139';
            if (errorMessage.includes('already associated')) {
                errorCode = 'ER20';
            }
        }
        return this.helper.promise.reject(errorCode);
    }
    skipValidation(email) {
        let bypassOnNextTry = false;
        if (this.helper.$emailInValidation.getValue() === email) {
            bypassOnNextTry = true;
            this.helper.$emailInValidation.next('');
        }
        else {
            this.helper.$emailInValidation.next(email);
        }
        return bypassOnNextTry;
    }
    async getAuthnHeaders(skip) {
        try {
            if (!skip) {
                await this.authN();
            }
            return {
                WCToken: this.helper.localStorage.getItem(CacheKeys.HEADERS_WC_TOKEN),
                WCTrustedToken: this.helper.localStorage.getItem(CacheKeys.HEADERS_WC_TRUSTED_TOKEN),
            };
        }
        catch (error) {
            throw error;
        }
    }
    async getInstanceId() {
        await this.createGuestInstance();
        return this.helper.getInstanceId();
    }
    async reset() {
        this.helper.clearGuest(true);
        try {
            const authNHeaders = await this.getAuthnHeaders(true);
            if (!isEmpty(authNHeaders)) {
                this.helper.clearAuthn();
                await this.authN();
            }
            else {
                await this.createGuestInstance();
            }
            this.helper.updateLoggedInStatus(false);
            return true;
        }
        catch (error) {
            throw error;
        }
    }
    ngOnDestroy() {
        this.subscriptions$?.unsubscribe();
    }
}
AccountMsGuestsClass.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AccountMsGuestsClass, deps: [{ token: 'AppDefaults' }, { token: AccountMsGuestsService }, { token: ProfileMsGuestsClass }, { token: MsGuestBaseHelper }, { token: i0.NgZone }, { token: i1$1.DalService }], target: i0.ɵɵFactoryTarget.Injectable });
AccountMsGuestsClass.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AccountMsGuestsClass, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AccountMsGuestsClass, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['AppDefaults']
                }] }, { type: AccountMsGuestsService }, { type: ProfileMsGuestsClass }, { type: MsGuestBaseHelper }, { type: i0.NgZone }, { type: i1$1.DalService }]; } });

class AccountMsGuestsModule {
}
AccountMsGuestsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AccountMsGuestsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AccountMsGuestsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AccountMsGuestsModule });
AccountMsGuestsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AccountMsGuestsModule, providers: [AccountMsGuestsClass, AccountMsGuestsService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AccountMsGuestsModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [AccountMsGuestsClass, AccountMsGuestsService]
                }]
        }] });

class EntitlementsMsGuestsService extends MsGuestsBaseService {
    getAssignees(guest_id) {
        return this.doRequest(this.urls.ENTITLEMENTS.ASSIGNEES + guest_id, this.TYPE_GET).then((response) => get(response, 'result.partyMembers', []));
    }
    createAssignee(assignee) {
        return this.doRequest(this.urls.ENTITLEMENTS.CREATE, this.TYPE_POST, assignee).then((response) => get(response, 'result.sequenceId'));
    }
    getTravelPartyMembers(guest_id) {
        return this.doRequest(this.replaceKey(this.urls.TRAVEL_PARTY.TRAVEL_PARTY_MEMBERS, {
            guest_id,
        }), this.TYPE_GET);
    }
    setTravelPartyMembers(guest_id, request, headers) {
        return this.doRequest(this.replaceKey(this.urls.TRAVEL_PARTY.TRAVEL_PARTY_MEMBERS, {
            guest_id,
        }), this.TYPE_POST, request, headers);
    }
    getEntitlementDetails(url) {
        return this.doRequest(url, this.TYPE_GET, null);
    }
    linkToGuestId(request) {
        return this.doRequest(this.urls.ENTITLEMENTS.LINK, this.TYPE_POST, request);
    }
    getLinkMethod(request) {
        return this.doRequest(this.urls.ENTITLEMENTS.ENTITLEMENTS_LINK_METHODS, this.TYPE_POST, request);
    }
    linkSearchLink(request) {
        return this.doRequest(this.urls.ENTITLEMENTS.ENTITLEMENTS_LINK_SEARCH_LINK, this.TYPE_POST, request);
    }
    loadTCMContent(IDList) {
        return this.doRequest(this.urls.TRIDION.ID_EQV_NAME + IDList, this.TYPE_GET);
    }
    linkTicket(guest_id, request, headers) {
        return this.doRequest(this.replaceKey(this.urls.WALLET.LINK_TICKET, { guest_id }), this.TYPE_POST, request, headers, undefined, undefined, true);
    }
}
EntitlementsMsGuestsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EntitlementsMsGuestsService, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
EntitlementsMsGuestsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EntitlementsMsGuestsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EntitlementsMsGuestsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class EntitlementsMsGuestsClass {
    constructor(service, helper) {
        this.service = service;
        this.helper = helper;
    }
    createAssignee(assignee, source_id, previousAssignees = [], retry = 3, guest_id = this.helper.getGuestId()) {
        if ((!guest_id && isEmpty(assignee)) || isEmpty(assignee) || !source_id) {
            return this.helper.promise.reject('Missing guest_id, source_id or assignee info: ', { guest_id, source_id, assignee });
        }
        let newSequenceId = 0;
        this.helper.spinner.show(SpinnerKeys.CREATE_ASSIGNEE);
        const getResponse = !this.helper.isAuth()
            ? Promise.resolve().then(() => {
                if (!isEmpty(previousAssignees)) {
                    previousAssignees = sortBy(previousAssignees, 'sequenceId');
                    newSequenceId =
                        (get(previousAssignees[previousAssignees.length - 1], 'sequenceId', 0)) + 1;
                }
                assignee.sequenceId = newSequenceId;
                previousAssignees.push(assignee);
                return this.getAssignees(undefined, previousAssignees);
            })
            : Promise.resolve().then(() => {
                const newAssignee = {
                    sourceId: source_id,
                    guestId: guest_id,
                    partyMemberFirstName: assignee.firstName,
                    partyMemberLastName: assignee.lastName,
                    partyMemberSuffix: assignee.suffix,
                };
                return this.service.createAssignee(newAssignee).then((sequenceId) => {
                    newSequenceId = sequenceId;
                    return this.getAssignees();
                });
            });
        return getResponse
            .then((assignees) => {
            this.helper.spinner.hide(SpinnerKeys.CREATE_ASSIGNEE);
            return {
                newSequenceId: newSequenceId,
                assignees: assignees,
            };
        })
            .catch((error) => {
            if (retry > 0) {
                return this.createAssignee(assignee, source_id, previousAssignees, retry - 1);
            }
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.promise.reject(error);
        });
    }
    getAssignees(guest_id = this.helper.getGuestId(), guestResponse) {
        if (!guest_id && isEmpty(guestResponse)) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        this.helper.spinner.show(SpinnerKeys.GET_ENTITLEMENT_ASSIGNEES);
        const getResponse = !isEmpty(guestResponse)
            ? Promise.resolve(guestResponse)
            : this.service.getAssignees(guest_id);
        return getResponse
            .then((assignees) => {
            this.helper.spinner.hide(SpinnerKeys.GET_ENTITLEMENT_ASSIGNEES);
            assignees = sortBy(assignees, 'firstName');
            assignees = map(assignees, (assignee, index) => {
                assignee.isOwner = assignee.sequenceId === 0;
                assignee.id = index;
                assignee.label = [
                    assignee.firstName,
                    assignee.lastName,
                    assignee.suffix,
                ]
                    .filter(Boolean)
                    .join(' ');
                return assignee;
            });
            assignees.push({
                suffix: '',
                firstName: '',
                sequenceId: 'add_new_name',
                label: get(this.helper.pageLabels, 'AddNewNameLabel'),
                lastName: get(this.helper.pageLabels, 'AddNewNameLabel'),
            });
            return assignees;
        })
            .catch((error) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.promise.reject(error);
        });
    }
    getTravelPartyMembers(guest_id = this.helper.getGuestId()) {
        this.helper.spinner.show(SpinnerKeys.GET_TRAVEL_PARTY_MEMBERS);
        return this.service
            .getTravelPartyMembers(guest_id)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.GET_TRAVEL_PARTY_MEMBERS);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER71');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    setTravelPartyMembers(request, guest_id = this.helper.getGuestId()) {
        this.helper.spinner.show(SpinnerKeys.SET_TRAVEL_PARTY_MEMBERS);
        const sourceId = this.helper.getSourceId('WebAssignEntitlements');
        const email = this.helper.getLoginUserName();
        const headers = {
            'x-source-id': sourceId,
            'iv-user': email,
        };
        return this.service
            .setTravelPartyMembers(guest_id, request, headers)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.SET_TRAVEL_PARTY_MEMBERS);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER71');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    getEntitlementDetails(ids) {
        this.helper.spinner.show(SpinnerKeys.GET_ENTITLEMENT_DETAILS);
        return this.service
            .getEntitlementDetails(this.helper.getRelativeUrl('/' + this.helper.relativeUrls.FETCH_TRIDION_ID_EQV_NAME + ids))
            .then(() => {
            this.helper.spinner.hide(SpinnerKeys.GET_ENTITLEMENT_DETAILS);
        })
            .catch(() => {
            this.helper.spinner.show(SpinnerKeys.OVERRIDE);
        });
    }
    getDetails(ids) {
        if (!ids) {
            return this.helper.promise.reject('Missing ids: ', { ids });
        }
        this.helper.spinner.show(SpinnerKeys.GET_ENTITLEMENT_DETAILS);
        return this.service
            .loadTCMContent(ids)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.GET_ENTITLEMENT_DETAILS);
            return get(response, 'result');
        })
            .catch((error) => {
            this.helper.showLabel('ER71');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    getLinkMethod(media_id, guest_id = this.helper.getGuestId()) {
        if (!media_id) {
            return this.helper.promise.reject('Missing media id: ', { media_id });
        }
        this.helper.spinner.show(SpinnerKeys.GET_LINK_ENTITLEMENT);
        return this.service
            .getLinkMethod({
            mediaId: media_id,
            guestId: guest_id,
        })
            .then((res) => {
            this.helper.spinner.hide(SpinnerKeys.GET_LINK_ENTITLEMENT);
            const result = get(res, 'result');
            const mediaStatus = get(res, 'result.mediaStatus', '');
            if (!result || !mediaStatus) {
                return;
            }
            if (mediaStatus !== 'Valid') {
                throw mediaStatus;
            }
            if (!result.name && !result.salesProgramId) {
                res = 'noSecondFactor';
            }
            else if (result.name) {
                res = 'name';
            }
            else if (result.salesProgramId) {
                res = 'salesProgramId';
            }
            return res;
        })
            .catch((mediaStatus) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.promise.reject(this.getEntitlementErrorCode(mediaStatus) || 'ER89');
        });
    }
    linkSearchLink(media_id) {
        if (!media_id) {
            return this.helper.promise.reject('Missing media id: ', { media_id });
        }
        this.helper.spinner.show(SpinnerKeys.ENTITLEMENTS_LINK_SEARCH_LINK);
        return this.service
            .linkSearchLink({ mediaId: media_id })
            .then((res) => {
            this.helper.spinner.hide(SpinnerKeys.ENTITLEMENTS_LINK_SEARCH_LINK);
            return get(res, 'result', []);
        })
            .catch((err) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.promise.reject(err);
        });
    }
    linkToGuestId(ids, source_id, sequence_id, invalidFactor = '', guest_id = this.helper.getGuestId()) {
        if (!ids) {
            return this.helper.promise.reject('Missing ids: ', { ids });
        }
        this.helper.spinner.show(SpinnerKeys.LINK_ENTITLEMENT);
        return this.service
            .linkToGuestId({
            mediaIds: ids,
            toGuestId: guest_id,
            sourceId: source_id,
            sequenceId: sequence_id,
        })
            .then((res) => {
            this.helper.spinner.hide(SpinnerKeys.LINK_ENTITLEMENT);
            const mediaStatus = get(res, 'result.mediaStatus');
            if (mediaStatus !== 'Valid') {
                return this.helper.promise.reject(res);
            }
            return get(res, 'result.entitlements');
        })
            .catch((err) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            const mediaStatus = get(err, 'result.mediaStatus');
            const salesIdError = invalidFactor === 'InvalidSecondFactorSalesId' &&
                mediaStatus === 'InvalidSecondFactor';
            return this.helper.promise.reject(salesIdError ? invalidFactor : err);
        });
    }
    linkTicket(request, headers) {
        this.helper.spinner.show(SpinnerKeys.LINK_ENTITLEMENT);
        const guest_id = this.helper.getGuestId();
        return this.service
            .linkTicket(guest_id, request, headers)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.LINK_ENTITLEMENT);
            return response;
        })
            .catch((err) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.promise.reject(err);
        });
    }
    getEntitlementErrorCode(status) {
        let error;
        switch (status) {
            case 'InvalidMedia':
            case 'NoEntitlementExists':
                error = 'ER89';
                break;
            case 'InvalidLinkEmployee':
                error = 'ER117';
                break;
            case 'TooManyEntitlements':
                error = 'ER77';
                break;
            case 'AccountLocked24Hour':
                error = 'ER100';
                break;
            case 'AccountLockedNew':
            case 'AccountLocked':
                error = 'ER92';
                break;
            case 'InvalidLinkRegistered':
                error = 'ER119';
                break;
            case 'InvalidGuest':
                error = 'ER71';
                break;
            case 'InvalidLinkExpired':
            case 'InvalidLinkRedeemed':
            case 'InvalidLinkCancelled':
                error = 'ER118';
                break;
            case 'InvalidSecondFactor':
                error = 'ER90';
                break;
            case 'InvalidSecondFactorSalesId':
                error = 'ER91';
                break;
            default:
                error = '';
        }
        return error;
    }
}
EntitlementsMsGuestsClass.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EntitlementsMsGuestsClass, deps: [{ token: EntitlementsMsGuestsService }, { token: MsGuestBaseHelper }], target: i0.ɵɵFactoryTarget.Injectable });
EntitlementsMsGuestsClass.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EntitlementsMsGuestsClass, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EntitlementsMsGuestsClass, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: EntitlementsMsGuestsService }, { type: MsGuestBaseHelper }]; } });

class EntitlementsMsGuestsModule {
}
EntitlementsMsGuestsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EntitlementsMsGuestsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
EntitlementsMsGuestsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EntitlementsMsGuestsModule });
EntitlementsMsGuestsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EntitlementsMsGuestsModule, providers: [EntitlementsMsGuestsClass, EntitlementsMsGuestsService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EntitlementsMsGuestsModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [EntitlementsMsGuestsClass, EntitlementsMsGuestsService]
                }]
        }] });

class PersonalMsGuestsService extends MsGuestsBaseService {
    getInfo(guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO, { guest_id }), this.TYPE_GET, undefined, headers);
    }
    getAddresses(guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_ADDRESSES, { guest_id }), this.TYPE_GET, undefined, headers);
    }
    createAddress(request, guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_ADDRESSES, { guest_id }), this.TYPE_POST, request, headers);
    }
    getAddress(guest_id, address_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_ADDRESSES_SINGLE, {
            guest_id,
            address_id,
        }), this.TYPE_GET, undefined, headers);
    }
    updateAddress(request, guest_id, address_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_ADDRESSES_SINGLE, {
            guest_id,
            address_id,
        }), this.TYPE_PUT, request, headers);
    }
    setEmailCommerceAddressRequest(url, request, headers) {
        return this.doRequest(url, this.TYPE_POST, request, headers);
    }
    deleteAddress(guest_id, address_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_ADDRESSES_SINGLE, {
            guest_id,
            address_id,
        }), this.TYPE_DELETE, undefined, headers);
    }
    getContactInfo(guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_CONTACTS, { guest_id }), this.TYPE_GET, undefined, headers);
    }
    updateContactInfo(request, guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_CONTACTS, { guest_id }), this.TYPE_PUT, request, headers);
    }
    getEmails(guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_EMAILS, { guest_id }), this.TYPE_GET, undefined, headers);
    }
    createEmail(request, guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_EMAILS, { guest_id }), this.TYPE_POST, request, headers);
    }
    getEmail(guest_id, email_address, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_EMAILS_SINGLE, {
            guest_id,
            email_address,
        }), this.TYPE_GET, undefined, headers);
    }
    updateEmail(request, guest_id, email_address, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_EMAILS_SINGLE, {
            guest_id,
            email_address,
        }), this.TYPE_PUT, request, headers);
    }
    deleteEmail(guest_id, email_address, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_EMAILS_SINGLE, {
            guest_id,
            email_address,
        }), this.TYPE_DELETE, undefined, headers);
    }
    getPhones(guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_PHONES, { guest_id }), this.TYPE_GET, undefined, headers);
    }
    createPhone(request, guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_PHONES, { guest_id }), this.TYPE_POST, request, headers);
    }
    getPhone(guest_id, phone_number, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_PHONES_SINGLE, {
            guest_id,
            phone_number,
        }), this.TYPE_GET, undefined, headers);
    }
    updatePhone(request, guest_id, phone_number, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_PHONES_SINGLE, {
            guest_id,
            phone_number,
        }), this.TYPE_PUT, request, headers);
    }
    deletePhone(guest_id, phone_number, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_PHONES_SINGLE, {
            guest_id,
            phone_number,
        }), this.TYPE_DELETE, undefined, headers);
    }
}
PersonalMsGuestsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PersonalMsGuestsService, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
PersonalMsGuestsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PersonalMsGuestsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PersonalMsGuestsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class PersonalMsGuestsClass {
    constructor(service, account, helper) {
        this.service = service;
        this.account = account;
        this.helper = helper;
    }
    getInfo(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        return this.service.getInfo(guest_id, headers);
    }
    getAddresses(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
        return this.service.getAddresses(guest_id, headers);
    }
    createAddress(request, sourceId, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        headers['x-source-id'] = sourceId;
        return this.service.createAddress(request, guest_id, headers);
    }
    getAddress(address_id, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id || !address_id) {
            return this.helper.promise.reject('Missing guest_id or address_id: ', {
                guest_id,
                address_id,
            });
        }
        return this.service.getAddress(guest_id, address_id, headers);
    }
    getPrimaryAddress() {
        this.helper.spinner.show(SpinnerKeys.GET_PRIMARY_ADDRESS);
        return this.getAddresses().then((request) => {
            this.helper.spinner.hide(SpinnerKeys.GET_PRIMARY_ADDRESS);
            if (!request || !request.addresses) {
                return;
            }
            const primaryAddress = find(request.addresses, (address) => {
                return address.addressId;
            });
            if (primaryAddress) {
                primaryAddress.primary =
                    DefaultValue.PRIMARY_SHIPPING_ADDRESS_INDICATOR;
            }
            return primaryAddress;
        });
    }
    updateAddress(request, sourceId, address_id, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id || !address_id || !etag) {
            return this.helper.promise.reject('Missing guest_id, address_id or etag: ', { guest_id, address_id, etag });
        }
        headers['x-source-id'] = sourceId;
        headers['If-Match'] = etag;
        this.helper.spinner.show(SpinnerKeys.UPDATE_PERSONAL_ADDRESS);
        return this.service
            .updateAddress(request, guest_id, address_id, headers)
            .then((response) => {
            this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
            this.helper.showLabel('SU9', 'success');
            this.helper.spinner.hide(SpinnerKeys.UPDATE_PERSONAL_ADDRESS);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER71');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    async setEmailCommerceAddressRequest(addressInfo) {
        this.helper.spinner.show(SpinnerKeys.SET_COMMERCE_ADDRESS_REQUEST);
        const contact = this.helper.addressToContact(addressInfo);
        contact.sourceId = this.helper.getSourceId('WebEditContactInfo');
        const request = {
            contact: [contact],
        };
        const headers = await this.account.getAuthnHeaders();
        return this.service
            .setEmailCommerceAddressRequest(this.helper.getRelativeUrl(this.helper.relativeUrls.SHIPPING_ADDRESS_SAVE), request, headers)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.SET_COMMERCE_ADDRESS_REQUEST);
            return { address: contact, addressId: response.addressId };
        })
            .catch((error) => {
            const isParamMissing = get(error, 'error.errors.0.errorKey') ===
                '_ERR_MISSING_CMD_PARAMETER';
            const isFlBillingError = get(error, 'error.errors.0.errorKey') === '_ERR_NOT_BILLING_ZIPCODE';
            let errorMessage = isParamMissing
                ? get(error, 'error.errors.0.errorMessage', '')
                : '';
            if (isFlBillingError) {
                errorMessage = this.helper.pageLabels.ER58;
            }
            if (!errorMessage) {
                errorMessage = get(error, 'data.message');
                const errors = get(error, 'data.rawError.errors', get(error, 'data.rawError', get(error, 'data.errors')));
                if (errorMessage) {
                    errorMessage = get(errors, '[0].errorMessage');
                }
            }
            this.helper.showLabel(errorMessage || this.helper.pageLabels.ER71);
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.promise.reject(errorMessage);
        });
    }
    deleteAddress(sourceId, address_id, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id || !address_id || !etag) {
            return this.helper.promise.reject('Missing guest_id, address_id or etag: ', { guest_id, address_id, etag });
        }
        headers['x-source-id'] = sourceId;
        headers['If-Match'] = etag;
        this.helper.spinner.show(SpinnerKeys.DELETE_PERSONAL_ADDRESS);
        return this.service
            .deleteAddress(guest_id, address_id, headers)
            .then((response) => {
            this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
            this.helper.showLabel('SU10', 'success');
            this.helper.spinner.hide(SpinnerKeys.DELETE_PERSONAL_ADDRESS);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER71');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    getContactInfo(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id');
        }
        return this.service.getContactInfo(guest_id, headers);
    }
    updateContactInfo(request, sourceId, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id || !etag) {
            return this.helper.promise.reject('Missing guest_id or etag: ', {
                guest_id,
                etag,
            });
        }
        headers['x-source-id'] = sourceId;
        headers['If-Match'] = etag;
        this.helper.spinner.show(SpinnerKeys.UPDATE_PERSONAL_CONTACT_INFO);
        return this.service
            .updateContactInfo(request, guest_id, headers)
            .then((response) => {
            this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
            this.helper.showLabel('SU7', 'success');
            this.helper.spinner.hide(SpinnerKeys.UPDATE_PERSONAL_CONTACT_INFO);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER84');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    getEmails(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        return this.service.getEmails(guest_id, headers);
    }
    createEmail(request, sourceId, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
        headers['x-source-id'] = sourceId;
        return this.service.createEmail(request, guest_id, headers);
    }
    getEmail(email_address, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id || !email_address) {
            return this.helper.promise.reject('Missing guest_id or email_address: ', {
                guest_id,
                email_address,
            });
        }
        return this.service.getEmail(guest_id, email_address, headers);
    }
    updateEmail(request, sourceId, email_address, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id || !email_address || !etag) {
            return this.helper.promise.reject('Missing guest_id, email_address or etag: ', { guest_id, email_address, etag });
        }
        headers['x-source-id'] = sourceId;
        headers['If-Match'] = etag;
        return this.service.updateEmail(request, guest_id, email_address, headers);
    }
    deleteEmail(sourceId, email_address, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id || !email_address || !etag) {
            return this.helper.promise.reject('Missing guest_id, email_address or etag: ', { guest_id, email_address, etag });
        }
        headers['x-source-id'] = sourceId;
        headers['If-Match'] = etag;
        return this.service.deleteEmail(guest_id, email_address, headers);
    }
    getPhones(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        return this.service.getPhones(guest_id, headers);
    }
    createPhone(request, sourceId, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
        headers['x-source-id'] = sourceId;
        return this.service.createPhone(request, guest_id, headers);
    }
    getPhone(phone_number, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id || !phone_number) {
            return this.helper.promise.reject('Missing guest_id or phone_number: ', {
                guest_id,
                phone_number,
            });
        }
        return this.service.getPhone(guest_id, phone_number, headers);
    }
    updatePhone(request, sourceId, phone_number, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id || !phone_number || !etag) {
            return this.helper.promise.reject('Missing guest_id, phone_number or etag: ', { guest_id, phone_number, etag });
        }
        headers['x-source-id'] = sourceId;
        headers['If-Match'] = etag;
        return this.service.updatePhone(request, guest_id, phone_number, headers);
    }
    deletePhone(sourceId, phone_number, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id || !phone_number || !etag) {
            return this.helper.promise.reject('Missing guest_id, phone_number or etag: ', { guest_id, phone_number, etag });
        }
        headers['x-source-id'] = sourceId;
        headers['If-Match'] = etag;
        return this.service.deletePhone(guest_id, phone_number, headers);
    }
}
PersonalMsGuestsClass.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PersonalMsGuestsClass, deps: [{ token: PersonalMsGuestsService }, { token: AccountMsGuestsClass }, { token: MsGuestBaseHelper }], target: i0.ɵɵFactoryTarget.Injectable });
PersonalMsGuestsClass.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PersonalMsGuestsClass, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PersonalMsGuestsClass, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: PersonalMsGuestsService }, { type: AccountMsGuestsClass }, { type: MsGuestBaseHelper }]; } });

class PersonalMsGuestsModule {
}
PersonalMsGuestsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PersonalMsGuestsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PersonalMsGuestsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PersonalMsGuestsModule });
PersonalMsGuestsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PersonalMsGuestsModule, providers: [PersonalMsGuestsClass, PersonalMsGuestsService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PersonalMsGuestsModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [PersonalMsGuestsClass, PersonalMsGuestsService]
                }]
        }] });

class PreferencesMsGuestsService extends MsGuestsBaseService {
    getPreferences(guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PREFERENCES, { guest_id }), this.TYPE_GET, undefined, headers);
    }
    createPreferences(request, guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PREFERENCES, { guest_id }), this.TYPE_POST, request, headers);
    }
    updatePreferences(request, guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.GUESTS.PREFERENCES, { guest_id }), this.TYPE_PUT, request, headers);
    }
}
PreferencesMsGuestsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PreferencesMsGuestsService, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
PreferencesMsGuestsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PreferencesMsGuestsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PreferencesMsGuestsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class PreferencesMsGuestsClass {
    constructor(service, helper) {
        this.service = service;
        this.helper = helper;
    }
    getPreferences(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        return this.service.getPreferences(guest_id, headers);
    }
    createPreferences(request, sourceId, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        headers['x-source-id'] = sourceId;
        return this.service.createPreferences(request, guest_id, headers);
    }
    updatePreferences(request, sourceId, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
        if (!guest_id || !etag) {
            return this.helper.promise.reject('Missing guest_id or etag: ', {
                guest_id,
                etag,
            });
        }
        headers['x-source-id'] = sourceId;
        headers['If-Match'] = etag;
        this.helper.spinner.show(SpinnerKeys.UPDATE_PREFERENCES);
        return this.service
            .updatePreferences(request, guest_id, headers)
            .then((response) => {
            this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
            this.helper.showLabel('SU6', 'success');
            this.helper.spinner.hide(SpinnerKeys.UPDATE_PREFERENCES);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER71');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
}
PreferencesMsGuestsClass.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PreferencesMsGuestsClass, deps: [{ token: PreferencesMsGuestsService }, { token: MsGuestBaseHelper }], target: i0.ɵɵFactoryTarget.Injectable });
PreferencesMsGuestsClass.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PreferencesMsGuestsClass, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PreferencesMsGuestsClass, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: PreferencesMsGuestsService }, { type: MsGuestBaseHelper }]; } });

class PreferencesMsGuestsModule {
}
PreferencesMsGuestsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PreferencesMsGuestsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PreferencesMsGuestsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PreferencesMsGuestsModule });
PreferencesMsGuestsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PreferencesMsGuestsModule, providers: [PreferencesMsGuestsClass, PreferencesMsGuestsService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PreferencesMsGuestsModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [PreferencesMsGuestsClass, PreferencesMsGuestsService]
                }]
        }] });

class ProfileMsGuestsModule {
}
ProfileMsGuestsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ProfileMsGuestsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ProfileMsGuestsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ProfileMsGuestsModule });
ProfileMsGuestsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ProfileMsGuestsModule, providers: [ProfileMsGuestsClass, ProfileMsGuestsService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ProfileMsGuestsModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [ProfileMsGuestsClass, ProfileMsGuestsService]
                }]
        }] });

class WalletMsGuestsService extends MsGuestsBaseService {
    loadCards(guest_id) {
        return this.doRequest(this.replaceKey(this.urls.FOLIO.GET_FOLIO, { guest_id }), this.TYPE_GET);
    }
    setCardPin(request, guest_id) {
        return this.doRequest(this.replaceKey(this.urls.FOLIO.SET_PIN, { guest_id }), this.TYPE_POST, request);
    }
    getTransactionHistory(guest_id) {
        return this.doRequest(this.replaceKey(this.urls.FOLIO.TRANSACTIONS, { guest_id }), this.TYPE_GET);
    }
    getFinancialTransactions(guest_id) {
        return this.doRequest(this.replaceKey(this.urls.TRAVEL_PARTY.TRANSACTIONS, { guest_id }), this.TYPE_GET);
    }
    addPaymentCard(request) {
        return this.doRequest(this.urls.FOLIO.ADD_CARD, this.TYPE_PUT, request);
    }
    deletePaymentCard(request, card_id, guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.FOLIO.DELETE_CARD, { card_id, guest_id }), this.TYPE_DELETE, request, headers);
    }
    setPrimaryPaymentCard(request) {
        return this.doRequest(this.urls.FOLIO.SET_PRIMARY_CARD, this.TYPE_PUT, request);
    }
    changeAlertPreferences(request, guest_id) {
        return this.doRequest(this.replaceKey(this.urls.FOLIO.ALERT_PREF, { guest_id }), this.TYPE_POST, request);
    }
    setSpendingLimit(request, guest_id) {
        return this.doRequest(this.replaceKey(this.urls.FOLIO.SPENDING_LIMIT, { guest_id }), this.TYPE_POST, request);
    }
    getWallet() {
        return this.doRequest(this.urls.FOLIO.WALLET, this.TYPE_GET);
    }
    getTickets(guest_id, resort_area_code) {
        return this.doRequest(this.replaceKey(this.urls.WALLET.TICKETS, { guest_id, resort_area_code }), this.TYPE_GET);
    }
    getPaymentCards(guest_id) {
        return this.doRequest(this.replaceKey(this.urls.WALLET.PAYMENT_CARDS, { guest_id }), this.TYPE_GET, undefined, undefined, undefined, undefined, undefined, true);
    }
    getPaymentCard(guest_id, payment_card_id) {
        return this.doRequest(this.replaceKey(this.urls.WALLET.PAYMENT_CARD, {
            guest_id,
            payment_card_id,
        }), this.TYPE_GET, undefined, undefined, undefined, undefined, undefined, true);
    }
    hasPin(guest_id) {
        return this.doRequest(this.replaceKey(this.urls.WALLET.HAS_PIN, { guest_id }), this.TYPE_GET, undefined, undefined, undefined, undefined, undefined, true);
    }
    setPin(request, guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.WALLET.SET_PIN, { guest_id }), this.TYPE_POST, request, headers, undefined, undefined, undefined, true);
    }
    setTravelPartySpendingLimit(request, member_id, guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.TRAVEL_PARTY.SET_SPENDING_LIMIT, {
            guest_id: guest_id,
            travel_party_member_id: member_id,
        }), this.TYPE_PUT, request, headers);
    }
    saveCard(request, guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.WALLET.PAYMENT_CARDS, {
            guest_id: guest_id,
        }), this.TYPE_POST, request, headers, undefined, undefined, undefined, true);
    }
    updateCard(request, guest_id, payment_card_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.WALLET.UPDATE_PAYMENT_CARD, {
            guest_id: guest_id,
            payment_card_id: payment_card_id,
        }), this.TYPE_PUT, request, headers, undefined, undefined, undefined, true);
    }
    setPrimaryCard(guest_id, payment_card_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.WALLET.MAKE_PRIMARY_CARD, {
            guest_id: guest_id,
            payment_card_id: payment_card_id,
        }), this.TYPE_POST, undefined, headers, undefined, undefined, undefined, true);
    }
    deleteCard(payment_card_id, guest_id, headers) {
        return this.doRequest(this.replaceKey(this.urls.WALLET.UPDATE_PAYMENT_CARD, {
            guest_id: guest_id,
            payment_card_id: payment_card_id,
        }), this.TYPE_DELETE, undefined, headers, undefined, undefined, undefined, true);
    }
}
WalletMsGuestsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WalletMsGuestsService, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
WalletMsGuestsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WalletMsGuestsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WalletMsGuestsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class WalletMsGuestsClass {
    constructor(service, helper, appDefaults) {
        this.service = service;
        this.helper = helper;
        this.appDefaults = appDefaults;
    }
    loadCards(guest_id = this.helper.getGuestId()) {
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        this.helper.spinner.show(SpinnerKeys.LOAD_CARDS);
        return this.service
            .loadCards(guest_id)
            .then((response) => {
            if (isEmpty(response)) {
                return this.helper.promise.reject('No response data: ', response);
            }
            this.helper.spinner.hide(SpinnerKeys.LOAD_CARDS);
            return response;
        })
            .catch((error) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    setCardPin(pin, isSameCreated, guest_id = this.helper.getGuestId()) {
        if (!guest_id || !pin) {
            return this.helper.promise.reject('Missing guest_id or pin:', {
                guest_id,
                pin,
            });
        }
        this.helper.spinner.show(SpinnerKeys.SET_CARD_PIN);
        const request = {
            pin,
            sourceId: this.helper.getSourceId('WebPIN'),
        };
        return this.service
            .setCardPin(request, guest_id)
            .then((response) => {
            this.helper.showLabel(isSameCreated ? 'SU14' : 'SU15', 'success');
            this.helper.spinner.hide(SpinnerKeys.SET_CARD_PIN);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel(isSameCreated ? 'ER69' : 'ER70');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError('Error submitting pin: ' + error);
        });
    }
    getTransactionHistory(guest_id = this.helper.getGuestId()) {
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        this.helper.spinner.show(SpinnerKeys.GET_TRANSACTION_HISTORY);
        return this.service
            .getTransactionHistory(guest_id)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.GET_TRANSACTION_HISTORY);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER94');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    getFinancialTransactions(guest_id = this.helper.getGuestId()) {
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        this.helper.spinner.show(SpinnerKeys.GET_TRANSACTION_HISTORY);
        return this.service
            .getFinancialTransactions(guest_id)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.GET_TRANSACTION_HISTORY);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER94');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    saveCardToAccount(cvc, accountNumber, expMonth, expireYear, sourceId, address, deleteCardId) {
        if (!address.address_id) {
            return this.helper.promise.reject('Address required to save card to account');
        }
        this.helper.spinner.show(SpinnerKeys.SAVE_CARD_TO_ACC);
        const requestPayload = {
            cardSecurityCode: cvc,
            addressId: address.address_id || '',
            currencyCode: DefaultValue.CURRENCY_CODE,
            paymentAccountNumber: accountNumber,
            expireMonth: expMonth,
            expireYear: expireYear,
            primary: address.primary,
            sourceId: sourceId,
        };
        if (deleteCardId) {
            requestPayload['deleteCardId'] = deleteCardId;
        }
        return this.addPaymentCard(requestPayload)
            .then(() => {
            this.helper.spinner.hide(SpinnerKeys.SAVE_CARD_TO_ACC);
            this.helper.showLabel('SU6', 'success');
        })
            .catch(() => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            this.helper.showLabel('ER71');
        });
    }
    addPaymentCard(request) {
        if (isEmpty(request)) {
            return this.helper.promise.reject('Missing request values: ', request);
        }
        if (!request.sourceId) {
            request.sourceId = this.helper.getSourceId('WebEditAddress');
        }
        this.helper.spinner.show(SpinnerKeys.ADD_PAYMENT_CARD);
        return this.service
            .addPaymentCard(request)
            .then((response) => {
            this.helper.showLabel('SU6', 'success');
            this.helper.spinner.hide(SpinnerKeys.ADD_PAYMENT_CARD);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER71');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    deletePaymentCard(cardId, guest_id = this.helper.getGuestId()) {
        if (!guest_id || !cardId) {
            return this.helper.promise.reject('Missing guest_id or cardId: ', {
                guest_id,
                cardId,
            });
        }
        const sourceId = this.helper.getSourceId('WebCard');
        const request = { cardId, sourceId };
        const headers = {
            'x-source-id': sourceId,
        };
        this.helper.spinner.show(SpinnerKeys.DELETE_PAYMENT_CARD);
        return this.service
            .deletePaymentCard(request, cardId, guest_id, headers)
            .then((response) => {
            this.helper.showLabel('SU21', 'success');
            this.helper.spinner.hide(SpinnerKeys.DELETE_PAYMENT_CARD);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER79');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    setPrimaryPaymentCard(cardId) {
        if (!cardId) {
            return this.helper.promise.reject('Missing cardId: ', { cardId });
        }
        const request = {
            cardId: cardId,
            sourceId: this.helper.getSourceId('WebCard'),
        };
        this.helper.spinner.show(SpinnerKeys.SET_PRIMARY_PAYMENT_CARD);
        return this.service
            .setPrimaryPaymentCard(request)
            .then(() => this.helper.spinner.hide(SpinnerKeys.SET_PRIMARY_PAYMENT_CARD))
            .catch((error) => {
            this.helper.showLabel('ER42');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    changeAlertPreferences(emailPreference, textPreference, guest_id = this.helper.getGuestId()) {
        const request = {
            email: emailPreference,
            text: !!textPreference,
            sourceId: this.helper.getSourceId('WebSpendLimit'),
        };
        this.helper.spinner.show(SpinnerKeys.CHANGE_ALERT_PREFERENCES);
        return this.service
            .changeAlertPreferences(request, guest_id)
            .then((response) => {
            this.helper.showLabel('SU19', 'success');
            this.helper.spinner.hide(SpinnerKeys.CHANGE_ALERT_PREFERENCES);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER93');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    setSpendingLimit(request, guest_id = this.helper.getGuestId()) {
        if (isEmpty(request)) {
            return this.helper.promise.reject('Missing request values: ', request);
        }
        if (!request.sourceId) {
            request.sourceId = this.helper.getSourceId('WebSpendLimit');
        }
        this.helper.spinner.show(SpinnerKeys.SET_SPENDING_LIMIT);
        return this.service
            .setSpendingLimit(request, guest_id)
            .then((response) => {
            if (response === '') {
                return this.helper.promise.reject('Failed to set spending limits: ', response);
            }
            this.helper.showLabel('SU18', 'success');
            this.helper.spinner.hide(SpinnerKeys.SET_SPENDING_LIMIT);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER71');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    getWallet() {
        this.helper.spinner.show(SpinnerKeys.GET_WALLET);
        return this.service
            .getWallet()
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.GET_WALLET);
            return get(response, 'result', {});
        })
            .catch((error) => {
            this.helper.showLabel('ER71');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    getTickets(guest_id, resort_area_code) {
        this.helper.spinner.show(SpinnerKeys.GET_TICKETS);
        return this.service
            .getTickets(guest_id, resort_area_code)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.GET_TICKETS);
            return response;
        })
            .catch((error) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            const statusCode = get(error, 'status', '');
            if (statusCode === 404) {
                return [];
            }
            this.helper.showLabel('ER71');
            return this.helper.catchError(error);
        });
    }
    getPaymentCards(guest_id = this.helper.getGuestId()) {
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        this.helper.spinner.show(SpinnerKeys.LOAD_CARDS);
        return this.service
            .getPaymentCards(guest_id)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.LOAD_CARDS);
            return response;
        })
            .catch((error) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    getPaymentCard(payment_card_id, guest_id = this.helper.getGuestId()) {
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        if (!payment_card_id) {
            return this.helper.promise.reject('Missing payment_card_id: ', {
                payment_card_id,
            });
        }
        this.helper.spinner.show(SpinnerKeys.GET_PAYMENT_CARD);
        return this.service
            .getPaymentCard(guest_id, payment_card_id)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.GET_PAYMENT_CARD);
            return response;
        })
            .catch((error) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    setPin(pin, isSameCreated, guest_id = this.helper.getGuestId()) {
        if (!guest_id || !pin) {
            return this.helper.promise.reject('Missing guest_id or pin: ', {
                guest_id,
                pin,
            });
        }
        this.helper.spinner.show(SpinnerKeys.SET_CARD_PIN);
        const request = {
            pin,
        };
        const headers = {
            'x-source-id': this.helper.getSourceId('WebPIN'),
        };
        return this.service
            .setPin(request, guest_id, headers)
            .then((response) => {
            console.log('success');
            this.helper.showLabel(isSameCreated ? 'SU14' : 'SU15', 'success');
            this.helper.spinner.hide(SpinnerKeys.SET_CARD_PIN);
            return response;
        })
            .catch((error) => {
            console.log('error!');
            this.helper.showLabel(isSameCreated ? 'ER69' : 'ER70');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError('Error submitting pin: ' + error);
        });
    }
    hasPin(guest_id = this.helper.getGuestId()) {
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        this.helper.spinner.show(SpinnerKeys.HAS_PIN);
        return this.service
            .hasPin(guest_id)
            .then((response) => {
            this.helper.spinner.hide(SpinnerKeys.HAS_PIN);
            return response;
        })
            .catch((error) => {
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    setTravelPartySpendingLimit(request, member_id, guest_id = this.helper.getGuestId()) {
        if (isEmpty(request)) {
            return this.helper.promise.reject('Missing request values: ', request);
        }
        this.helper.spinner.show(SpinnerKeys.SET_SPENDING_LIMIT);
        const headers = {
            'x-source-id': this.helper.getSourceId('WebSpendLimit'),
        };
        return this.service
            .setTravelPartySpendingLimit(request, member_id, guest_id, headers)
            .then((response) => {
            if (response === '') {
                return this.helper.promise.reject('Failed to set spending limits: ', response);
            }
            this.helper.showLabel('SU18', 'success');
            this.helper.spinner.hide(SpinnerKeys.SET_SPENDING_LIMIT);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER71');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    saveCard(request, guest_id = this.helper.getGuestId()) {
        if (isEmpty(request)) {
            return this.helper.promise.reject('Missing request values: ', request);
        }
        if (!guest_id) {
            return this.helper.promise.reject('Missing guest_id: ', { guest_id });
        }
        const { UPR_MOBILE_APIKEY, RESORT_AREA_CODE, PERSONALIZE_RESORT_CODE } = this.appDefaults;
        const headers = {
            'x-source-id': this.helper.getSourceId('WebEditAddress'),
            'x-instance-id': this.helper.getInstanceId(),
            'x-uniwebservice-apikey': UPR_MOBILE_APIKEY,
            'x-resort-area-code': RESORT_AREA_CODE ?? PERSONALIZE_RESORT_CODE,
        };
        this.helper.spinner.show(SpinnerKeys.SAVE_CARD_TO_ACC);
        return this.service
            .saveCard(request, guest_id, headers)
            .then(() => {
            this.helper.spinner.hide(SpinnerKeys.SAVE_CARD_TO_ACC);
            this.helper.showLabel('SU6', 'success');
        })
            .catch((error) => {
            get(error, 'status') === 400
                ? this.helper.showLabel('ER35')
                : this.helper.showLabel('ER71');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    updateCard(request, paymentCardId, guestId = this.helper.getGuestId(), etag = this.helper.getEtag()) {
        if (isEmpty(request)) {
            return this.helper.promise.reject('Missing request values: ', request);
        }
        if (!guestId) {
            return this.helper.promise.reject('Missing guest_id: ', { guestId });
        }
        if (!paymentCardId) {
            return this.helper.promise.reject('Missing payment_card_id: ', {
                paymentCardId,
            });
        }
        const { RESORT_AREA_CODE, PERSONALIZE_RESORT_CODE } = this.appDefaults;
        const headers = {
            'x-source-id': this.helper.getSourceId('WebEditAddress'),
            'x-resort-area-code': RESORT_AREA_CODE ?? PERSONALIZE_RESORT_CODE,
            'If-Match': etag,
        };
        this.helper.spinner.show(SpinnerKeys.SAVE_CARD_TO_ACC);
        return this.service
            .updateCard(request, guestId, paymentCardId, headers)
            .then(() => {
            this.helper.spinner.hide(SpinnerKeys.SAVE_CARD_TO_ACC);
            this.helper.showLabel('SU6', 'success');
        })
            .catch((error) => {
            get(error, 'status') === 400
                ? this.helper.showLabel('ER35')
                : this.helper.showLabel('ER71');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    setPrimaryCard(paymentCardId, guestId = this.helper.getGuestId()) {
        if (!guestId) {
            return this.helper.promise.reject('Missing guest_id: ', { guestId });
        }
        if (!paymentCardId) {
            return this.helper.promise.reject('Missing payment_card_id: ', {
                paymentCardId,
            });
        }
        const headers = {
            'x-source-id': this.helper.getSourceId('WebCard'),
        };
        this.helper.spinner.show(SpinnerKeys.SET_PRIMARY_PAYMENT_CARD);
        return this.service
            .setPrimaryCard(guestId, paymentCardId, headers)
            .then(() => this.helper.spinner.hide(SpinnerKeys.SET_PRIMARY_PAYMENT_CARD))
            .catch((error) => {
            this.helper.showLabel('ER42');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
    deleteCard(cardId, guestId = this.helper.getGuestId(), etag = this.helper.getEtag()) {
        if (!guestId) {
            return this.helper.promise.reject('Missing guest_id: ', { guestId });
        }
        if (!cardId) {
            return this.helper.promise.reject('Missing payment_card_id: ', {
                cardId,
            });
        }
        const headers = {
            'x-source-id': this.helper.getSourceId('WebCard'),
            'if-match': etag,
        };
        this.helper.spinner.show(SpinnerKeys.DELETE_PAYMENT_CARD);
        return this.service
            .deleteCard(cardId, guestId, headers)
            .then((response) => {
            this.helper.showLabel('SU21', 'success');
            this.helper.spinner.hide(SpinnerKeys.DELETE_PAYMENT_CARD);
            return response;
        })
            .catch((error) => {
            this.helper.showLabel('ER79');
            this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
            return this.helper.catchError(error);
        });
    }
}
WalletMsGuestsClass.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WalletMsGuestsClass, deps: [{ token: WalletMsGuestsService }, { token: MsGuestBaseHelper }, { token: 'AppDefaults' }], target: i0.ɵɵFactoryTarget.Injectable });
WalletMsGuestsClass.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WalletMsGuestsClass, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WalletMsGuestsClass, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: WalletMsGuestsService }, { type: MsGuestBaseHelper }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['AppDefaults']
                }] }]; } });

class WalletMsGuestsModule {
}
WalletMsGuestsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WalletMsGuestsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
WalletMsGuestsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WalletMsGuestsModule });
WalletMsGuestsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WalletMsGuestsModule, providers: [WalletMsGuestsClass, WalletMsGuestsService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WalletMsGuestsModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [WalletMsGuestsClass, WalletMsGuestsService]
                }]
        }] });

class MsGuestModule {
}
MsGuestModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsGuestModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MsGuestModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsGuestModule, imports: [PageLabelsModule,
        DalModule,
        NotificationModule,
        SpinnerModule,
        AccountMsGuestsModule,
        EntitlementsMsGuestsModule,
        PersonalMsGuestsModule,
        PreferencesMsGuestsModule,
        ProfileMsGuestsModule,
        WalletMsGuestsModule] });
MsGuestModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsGuestModule, imports: [[
            PageLabelsModule,
            DalModule,
            NotificationModule,
            SpinnerModule,
            AccountMsGuestsModule,
            EntitlementsMsGuestsModule,
            PersonalMsGuestsModule,
            PreferencesMsGuestsModule,
            ProfileMsGuestsModule,
            WalletMsGuestsModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsGuestModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        PageLabelsModule,
                        DalModule,
                        NotificationModule,
                        SpinnerModule,
                        AccountMsGuestsModule,
                        EntitlementsMsGuestsModule,
                        PersonalMsGuestsModule,
                        PreferencesMsGuestsModule,
                        ProfileMsGuestsModule,
                        WalletMsGuestsModule
                    ]
                }]
        }] });

class MsGuestsService {
    constructor(account, entitlements, profile, personal, preferences, wallet, helper) {
        this.account = account;
        this.entitlements = entitlements;
        this.profile = profile;
        this.personal = personal;
        this.preferences = preferences;
        this.wallet = wallet;
        this.helper = helper;
    }
}
MsGuestsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsGuestsService, deps: [{ token: AccountMsGuestsClass }, { token: EntitlementsMsGuestsClass }, { token: ProfileMsGuestsClass }, { token: PersonalMsGuestsClass }, { token: PreferencesMsGuestsClass }, { token: WalletMsGuestsClass }, { token: MsGuestBaseHelper }], target: i0.ɵɵFactoryTarget.Injectable });
MsGuestsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsGuestsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MsGuestsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: AccountMsGuestsClass }, { type: EntitlementsMsGuestsClass }, { type: ProfileMsGuestsClass }, { type: PersonalMsGuestsClass }, { type: PreferencesMsGuestsClass }, { type: WalletMsGuestsClass }, { type: MsGuestBaseHelper }]; } });

export { AddressType, CacheKeys, DefaultValue, EmailType, GuestsUrls, MS_GUESTS_ENV_DEFAULTS, MS_GUESTS_REPLACED_DEFAULTS, MsGuestModule, MsGuestsService, PhoneType, SourceIds, SpinnerKeys, StatusType, USHSourceIds };
